import React, { useState, useEffect, useCallback } from 'react';
import { DateTime } from 'luxon';
import ClimateMap from './ClimateMap';
import TimeseriesImporter from './TimeseriesImporter';
import { loadDefinitions, CATEGORIES } from '../store/assetDefinitions';
import ShapePreview from './ShapePreview';
import DatasetSummary from './DatasetSummary';
import DatasetView from './DatasetView';

const API = process.env.REACT_APP_API_URL || `http://${window.location.hostname}:5001`;

const STATUS_COLORS = {
  active: '#4ecca3',
  draft: '#F59E0B',
  archived: '#8a9a95',
  review: '#60a5fa',
  approved: '#4ecca3',
  superseded: '#8a9a95',
};

const REV_STATUS_OPTIONS = ['draft', 'review', 'approved', 'superseded'];
const VERSION_STATUS_OPTIONS = ['draft', 'review', 'approved', 'superseded'];

// Climate variables recognised at the project level. Must stay in sync with
// backend KNOWN_VARIABLES in /api/projects/:pid/climate-sources/candidates.
const CLIMATE_VARIABLES = [
  { id: 'temperature', label: 'Temperature', required: true,  unit: '°C',  group: 'required' },
  { id: 'pressure',    label: 'Pressure',    required: true,  unit: 'hPa', group: 'required' },
  { id: 'humidity',    label: 'Humidity',    required: true,  unit: '%',   group: 'required' },
  { id: 'ghi',         label: 'GHI',         required: false, unit: 'W/m²', group: 'solar', note: 'Global Horizontal Irradiance' },
  { id: 'dni',         label: 'DNI',         required: false, unit: 'W/m²', group: 'solar', note: 'Direct Normal Irradiance' },
];

// Short badge color per climate source family
const SOURCE_COLORS = {
  'PVGIS-SARAH2': '#d97706',
  'PVGIS-SARAH3': '#d97706',
  'PVGIS-ERA5':   '#60a5fa',
  'OPEN-METEO':   '#4ecca3',
};

const sourceColor = (src) => {
  if (!src) return '#94a3b8';
  for (const key of Object.keys(SOURCE_COLORS)) {
    if (src.includes(key)) return SOURCE_COLORS[key];
  }
  return '#94a3b8';
};

// Format an ISO timestamp to "Apr 5, 15:10" style short form
const fmtTs = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch {
    return iso;
  }
};

const fmtDate = (iso) => {
  if (!iso) return '';
  return iso.slice(0, 10);
};

const ProjectsView = ({ onOpenCanvas }) => {
  const [projects, setProjects] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [selectedProject, setSelectedProject] = useState(null);
  const [formData, setFormData] = useState({
    project_code: '', name: '', description: '', client: '', location_id: '', status: 'active',
  });

  // Version + revision state (detail view)
  const [versions, setVersions] = useState([]);
  const [revisionsByVersion, setRevisionsByVersion] = useState({});
  const [expandedVersionId, setExpandedVersionId] = useState(null);
  const [expandedRevisionId, setExpandedRevisionId] = useState(null);

  const [showVersionForm, setShowVersionForm] = useState(false);
  const [versionForm, setVersionForm] = useState({ name: '', description: '', version_date: '', status: 'draft' });
  const [editingVersionId, setEditingVersionId] = useState(null);

  // Climate sources state (project-level)
  const [climateSources, setClimateSources] = useState([]); // current mapping rows from GET endpoint
  const [climateCandidates, setClimateCandidates] = useState({}); // {variable: [dataset...]}
  const [climateConfigMode, setClimateConfigMode] = useState(false);
  const [projectLocation, setProjectLocation] = useState(null); // full locations row with lat/lon
  const [availableSources, setAvailableSources] = useState([]); // filtered to only sources at this location

  // Project assets state
  const [projectAssets, setProjectAssets] = useState([]);
  const [assetDefinitions, setAssetDefinitions] = useState([]);
  const [assetsConfigMode, setAssetsConfigMode] = useState(false);

  // Project nodes state
  const [projectNodes, setProjectNodes] = useState([]);
  const [nodeDefinitions, setNodeDefinitions] = useState([]);
  const [nodesConfigMode, setNodesConfigMode] = useState(false);

  // Project lines state
  const [projectLines, setProjectLines] = useState([]);
  const [lineDefinitions, setLineDefinitions] = useState([]);
  const [linesConfigMode, setLinesConfigMode] = useState(false);

  // Dataset section (energyPRO result datasets)
  const [datasetMode, setDatasetMode] = useState(false);

  // Two-step delete confirms: `${type}:${id}`
  const [confirmDelete, setConfirmDelete] = useState(null);

  const fetchProjects = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/projects`);
      if (res.ok) setProjects(await res.json());
    } catch {}
    setLoading(false);
  }, []);

  const fetchLocations = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/locations`);
      if (res.ok) setLocations(await res.json());
    } catch {}
  }, []);

  useEffect(() => { fetchProjects(); fetchLocations(); }, [fetchProjects, fetchLocations]);

  // ---- Version + revision API helpers ------------------------------------
  const fetchVersions = useCallback(async (pid) => {
    try {
      const res = await fetch(`${API}/api/projects/${pid}/versions`);
      if (res.ok) setVersions(await res.json());
    } catch {}
  }, []);

  const fetchRevisions = useCallback(async (vid) => {
    try {
      const res = await fetch(`${API}/api/versions/${vid}/revisions`);
      if (res.ok) {
        const list = await res.json();
        setRevisionsByVersion((prev) => ({ ...prev, [vid]: list }));
      }
    } catch {}
  }, []);

  const fetchClimateSources = useCallback(async (pid) => {
    try {
      const res = await fetch(`${API}/api/projects/${pid}/climate-sources`);
      if (res.ok) setClimateSources(await res.json());
    } catch {}
  }, []);

  const fetchClimateCandidates = useCallback(async (pid) => {
    try {
      const res = await fetch(`${API}/api/projects/${pid}/climate-sources/candidates`);
      if (res.ok) setClimateCandidates(await res.json());
    } catch {}
  }, []);

  const saveClimateSources = async (mapping) => {
    try {
      const res = await fetch(`${API}/api/projects/${selectedProject.id}/climate-sources`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(mapping),
      });
      if (res.ok) {
        setClimateSources(await res.json());
        setClimateConfigMode(false);
      }
    } catch {}
  };

  // Enter the climate config sub-screen: fetch the project's location + the
  // list of integrated sources near it, then filter to just the ones actually
  // present in this location's downloaded datasets.
  const enterClimateConfig = useCallback(async () => {
    if (!selectedProject?.location_id) return;
    try {
      // 1. Find the full location row (has latitude/longitude)
      const locRes = await fetch(`${API}/api/locations`);
      const allLocs = locRes.ok ? await locRes.json() : [];
      const loc = allLocs.find((l) => l.id === selectedProject.location_id);
      setProjectLocation(loc || null);

      if (loc && loc.latitude != null && loc.longitude != null) {
        // 2. Fetch availability for this coordinate (endpoint wraps response in {sources: [...]})
        const availRes = await fetch(`${API}/api/sources/availability?lat=${loc.latitude}&lon=${loc.longitude}`);
        const availBody = availRes.ok ? await availRes.json() : {};
        const allSources = Array.isArray(availBody) ? availBody : (availBody.sources || []);

        // 3. Derive the set of source IDs actually downloaded at this location
        //    from climateCandidates (union across all variables, split composites on '+')
        const usedSet = new Set();
        Object.values(climateCandidates || {}).forEach((list) => {
          (list || []).forEach((d) => {
            if (d.source) {
              d.source.split('+').forEach((part) => usedSet.add(part.trim()));
            }
          });
        });

        // 4. Filter availability to only sources that appear in downloaded datasets
        const filtered = (allSources || []).filter((s) => {
          if (!s.id) return false;
          return usedSet.has(s.id);
        });
        setAvailableSources(filtered);
      } else {
        setAvailableSources([]);
      }
      setClimateConfigMode(true);
    } catch (err) {
      console.error('enterClimateConfig failed:', err);
      // Still enter the sub-screen so the user isn't stranded
      setClimateConfigMode(true);
    }
  }, [selectedProject, climateCandidates]);

  const fetchProjectAssets = useCallback(async (pid) => {
    try {
      const res = await fetch(`${API}/api/projects/${pid}/assets`);
      if (res.ok) setProjectAssets(await res.json());
    } catch {}
  }, []);

  const fetchAssetDefinitions = useCallback(async () => {
    try {
      const defs = await loadDefinitions();
      setAssetDefinitions(defs || []);
    } catch {}
  }, []);

  const createProjectAsset = async (assetDefId, name) => {
    try {
      const res = await fetch(`${API}/api/projects/${selectedProject.id}/assets`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ asset_def_id: assetDefId, name }),
      });
      if (res.ok) {
        const created = await res.json();
        await fetchProjectAssets(selectedProject.id);
        return created;
      }
    } catch {}
    return null;
  };

  const updateProjectAsset = async (aid, patch) => {
    try {
      const res = await fetch(`${API}/api/project-assets/${aid}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(patch),
      });
      if (res.ok) {
        await fetchProjectAssets(selectedProject.id);
        return await res.json();
      }
    } catch {}
    return null;
  };

  const deleteProjectAsset = async (aid) => {
    try {
      await fetch(`${API}/api/project-assets/${aid}`, { method: 'DELETE' });
      await fetchProjectAssets(selectedProject.id);
    } catch {}
  };

  // --- Node helpers ---
  const fetchProjectNodes = useCallback(async (pid) => {
    try {
      const res = await fetch(`${API}/api/projects/${pid}/nodes`);
      if (res.ok) setProjectNodes(await res.json());
    } catch {}
  }, []);
  const fetchNodeDefinitions = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/node-definitions`);
      if (res.ok) setNodeDefinitions(await res.json());
    } catch {}
  }, []);
  const createProjectNode = async (defId, name) => {
    try {
      const res = await fetch(`${API}/api/projects/${selectedProject.id}/nodes`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ node_def_id: defId, name }),
      });
      if (res.ok) { await fetchProjectNodes(selectedProject.id); return await res.json(); }
    } catch {} return null;
  };
  const updateProjectNode = async (nid, patch) => {
    try {
      const res = await fetch(`${API}/api/project-nodes/${nid}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(patch),
      });
      if (res.ok) { await fetchProjectNodes(selectedProject.id); return await res.json(); }
    } catch {} return null;
  };
  const deleteProjectNode = async (nid) => {
    try { await fetch(`${API}/api/project-nodes/${nid}`, { method: 'DELETE' }); await fetchProjectNodes(selectedProject.id); } catch {}
  };

  // --- Line helpers ---
  const fetchProjectLines = useCallback(async (pid) => {
    try {
      const res = await fetch(`${API}/api/projects/${pid}/lines`);
      if (res.ok) setProjectLines(await res.json());
    } catch {}
  }, []);
  const fetchLineDefinitions = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/line-definitions`);
      if (res.ok) setLineDefinitions(await res.json());
    } catch {}
  }, []);
  const createProjectLine = async (defId, name) => {
    try {
      const res = await fetch(`${API}/api/projects/${selectedProject.id}/lines`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ line_def_id: defId, name }),
      });
      if (res.ok) { await fetchProjectLines(selectedProject.id); return await res.json(); }
    } catch {} return null;
  };
  const updateProjectLine = async (lid, patch) => {
    try {
      const res = await fetch(`${API}/api/project-lines/${lid}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(patch),
      });
      if (res.ok) { await fetchProjectLines(selectedProject.id); return await res.json(); }
    } catch {} return null;
  };
  const deleteProjectLine = async (lid) => {
    try { await fetch(`${API}/api/project-lines/${lid}`, { method: 'DELETE' }); await fetchProjectLines(selectedProject.id); } catch {}
  };

  const fetchProjectDetail = useCallback(async (pid) => {
    try {
      const res = await fetch(`${API}/api/projects/${pid}`);
      if (res.ok) {
        const proj = await res.json();
        setSelectedProject(proj);
        await fetchVersions(pid);
        await fetchClimateSources(pid);
        await fetchClimateCandidates(pid);
        await fetchProjectAssets(pid);
        await fetchAssetDefinitions();
        await fetchProjectNodes(pid);
        await fetchNodeDefinitions();
        await fetchProjectLines(pid);
        await fetchLineDefinitions();
      }
    } catch {}
  }, [fetchVersions, fetchClimateSources, fetchClimateCandidates, fetchProjectAssets, fetchAssetDefinitions, fetchProjectNodes, fetchNodeDefinitions, fetchProjectLines, fetchLineDefinitions]);

  const openNewVersionForm = () => {
    setVersionForm({
      name: '',
      description: '',
      version_date: new Date().toISOString().slice(0, 10),
      status: 'draft',
    });
    setShowVersionForm(true);
  };

  const createVersionFromForm = async (e) => {
    e.preventDefault();
    const payload = {
      name: versionForm.name.trim(),
      description: versionForm.description.trim() || null,
      version_date: versionForm.version_date || null,
      status: versionForm.status,
    };
    try {
      const res = await fetch(`${API}/api/projects/${selectedProject.id}/versions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (res.ok) {
        setShowVersionForm(false);
        await fetchVersions(selectedProject.id);
        const v = await res.json();
        setExpandedVersionId(v.id);
        fetchRevisions(v.id);
      }
    } catch (err) { console.error(err); }
  };

  const updateVersion = async (vid, patch) => {
    try {
      const res = await fetch(`${API}/api/versions/${vid}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(patch),
      });
      if (res.ok) {
        await fetchVersions(selectedProject.id);
        setEditingVersionId(null);
      }
    } catch {}
  };

  const beginEditVersion = (vid) => {
    setExpandedVersionId(vid);
    setEditingVersionId(vid);
    if (!revisionsByVersion[vid]) fetchRevisions(vid);
  };

  const deleteVersion = async (vid) => {
    try {
      await fetch(`${API}/api/versions/${vid}`, { method: 'DELETE' });
      setConfirmDelete(null);
      if (expandedVersionId === vid) setExpandedVersionId(null);
      await fetchVersions(selectedProject.id);
    } catch {}
  };

  const createRevision = async (vid) => {
    try {
      const res = await fetch(`${API}/api/versions/${vid}/revisions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ description: '' }),
      });
      if (res.ok) {
        await fetchRevisions(vid);
        await fetchVersions(selectedProject.id); // refresh revision count
      } else if (res.status === 409) {
        const body = await res.json();
        alert(body.error || 'Max revisions reached');
      }
    } catch {}
  };

  const updateRevision = async (rid, patch) => {
    try {
      const res = await fetch(`${API}/api/revisions/${rid}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(patch),
      });
      if (res.ok) {
        const updated = await res.json();
        setRevisionsByVersion((prev) => ({
          ...prev,
          [updated.version_id]: (prev[updated.version_id] || []).map((r) => r.id === rid ? updated : r),
        }));
      }
    } catch {}
  };

  const deleteRevision = async (vid, rid) => {
    try {
      await fetch(`${API}/api/revisions/${rid}`, { method: 'DELETE' });
      setConfirmDelete(null);
      if (expandedRevisionId === rid) setExpandedRevisionId(null);
      await fetchRevisions(vid);
      await fetchVersions(selectedProject.id);
    } catch {}
  };

  // ---- Project form handlers (existing) ----------------------------------
  const handleFormChange = (e) => {
    const { name, value } = e.target;
    if (name === 'project_code') {
      setFormData((prev) => ({ ...prev, [name]: value.toUpperCase().slice(0, 5) }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    const payload = { ...formData, location_id: formData.location_id ? parseInt(formData.location_id) : null };
    try {
      const url = editingId ? `${API}/api/projects/${editingId}` : `${API}/api/projects`;
      const res = await fetch(url, {
        method: editingId ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (res.ok) {
        setShowForm(false);
        setEditingId(null);
        await fetchProjects();
        if (!editingId) {
          const saved = await res.json();
          fetchProjectDetail(saved.id);
        }
      }
    } catch (e) { console.error(e); }
  };

  const handleEdit = (proj) => {
    setFormData({
      project_code: proj.project_code || '',
      name: proj.name || '',
      description: proj.description || '',
      client: proj.client || '',
      location_id: proj.location_id?.toString() || '',
      status: proj.status || 'active',
    });
    setEditingId(proj.id);
    setShowForm(true);
  };

  const handleDelete = async (pid) => {
    if (!window.confirm('Delete this project and all its versions/revisions?')) return;
    try {
      await fetch(`${API}/api/projects/${pid}`, { method: 'DELETE' });
      setSelectedProject(null);
      fetchProjects();
    } catch {}
  };

  const handleNewProject = () => {
    setFormData({ project_code: '', name: '', description: '', client: '', location_id: '', status: 'active' });
    setEditingId(null);
    setShowForm(true);
  };

  const openCanvasViewer = (version, revision) => {
    if (onOpenCanvas) {
      onOpenCanvas({
        projectId: selectedProject.id,
        projectName: selectedProject.name,
        versionId: version.id,
        versionName: `v${version.version_number} — ${version.name}`,
        revisionId: revision.id,
        revLetter: revision.rev_letter,
      });
    }
  };

  // ========================================================================
  // Climate config sub-screen (replaces project detail while active)
  // ========================================================================
  if (selectedProject && climateConfigMode) {
    return (
      <ClimateConfigScreen
        project={selectedProject}
        location={projectLocation}
        sources={climateSources}
        candidates={climateCandidates}
        availableSources={availableSources}
        onSave={saveClimateSources}
        onBack={() => setClimateConfigMode(false)}
      />
    );
  }

  // ========================================================================
  // Assets config sub-screen
  // ========================================================================
  if (selectedProject && assetsConfigMode) {
    return (
      <AssetsConfigScreen
        project={selectedProject}
        assets={projectAssets}
        definitions={assetDefinitions}
        onCreate={createProjectAsset}
        onUpdate={updateProjectAsset}
        onDelete={deleteProjectAsset}
        onBack={() => setAssetsConfigMode(false)}
      />
    );
  }

  // ========================================================================
  // Nodes config sub-screen
  // ========================================================================
  if (selectedProject && nodesConfigMode) {
    return (
      <GenericItemConfigScreen
        project={selectedProject}
        items={projectNodes}
        definitions={nodeDefinitions}
        itemType="node"
        itemLabel="Nodes"
        defIdField="node_def_id"
        onCreate={createProjectNode}
        onUpdate={updateProjectNode}
        onDelete={deleteProjectNode}
        onBack={() => setNodesConfigMode(false)}
      />
    );
  }

  // ========================================================================
  // Lines config sub-screen
  // ========================================================================
  if (selectedProject && linesConfigMode) {
    return (
      <GenericItemConfigScreen
        project={selectedProject}
        items={projectLines}
        definitions={lineDefinitions}
        itemType="line"
        itemLabel="Lines"
        defIdField="line_def_id"
        onCreate={createProjectLine}
        onUpdate={updateProjectLine}
        onDelete={deleteProjectLine}
        onBack={() => setLinesConfigMode(false)}
      />
    );
  }

  // ========================================================================
  // Dataset sub-screen
  // ========================================================================
  if (selectedProject && datasetMode) {
    return (
      <DatasetView
        project={selectedProject}
        onBack={() => setDatasetMode(false)}
      />
    );
  }

  // ========================================================================
  // Project detail view
  // ========================================================================
  if (selectedProject) {
    const p = selectedProject;
    const totalRevisions = versions.reduce((s, v) => s + (v.revision_count || 0), 0);

    return (
      <div className="def-list-view">
        <div className="def-list-header">
          <div>
            <h1>
              <span className="location-code-badge" style={{ fontSize: 14 }}>{p.project_code}</span>
              {' '}{p.name}
            </h1>
            <p className="def-list-subtitle">{p.description}</p>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn-ghost" onClick={() => { setSelectedProject(null); setVersions([]); setRevisionsByVersion({}); }}>{'← Back to Projects'}</button>
            <button className="btn-ghost" onClick={() => handleEdit(p)}>Edit</button>
          </div>
        </div>

        <div style={{ padding: '0 16px' }}>
          {/* KPI strip */}
          <div className="kpi-grid" style={{ marginBottom: 12 }}>
            <div className="kpi-card">
              <span className="kpi-value">{versions.length}</span>
              <span className="kpi-label">Versions</span>
            </div>
            <div className="kpi-card">
              <span className="kpi-value">{totalRevisions}</span>
              <span className="kpi-label">Total Revisions</span>
            </div>
            <div className="kpi-card">
              <span className="kpi-value" style={{ color: STATUS_COLORS[p.status] || '#fff' }}>{p.status}</span>
              <span className="kpi-label">Status</span>
            </div>
            {p.client && (
              <div className="kpi-card">
                <span className="kpi-value" style={{ fontSize: 16 }}>{p.client}</span>
                <span className="kpi-label">Client</span>
              </div>
            )}
          </div>

          {/* Climate Data */}
          <ClimateDataSummary
            project={p}
            sources={climateSources}
            onConfigure={enterClimateConfig}
          />

          {/* Assets */}
          <AssetsSummary
            project={p}
            assets={projectAssets}
            onConfigure={() => setAssetsConfigMode(true)}
          />

          {/* Nodes */}
          <GenericItemSummary
            label="Nodes"
            items={projectNodes}
            onConfigure={() => setNodesConfigMode(true)}
          />

          {/* Lines */}
          <GenericItemSummary
            label="Lines"
            items={projectLines}
            onConfigure={() => setLinesConfigMode(true)}
          />

          {/* Dataset */}
          <DatasetSummary
            project={p}
            onOpen={() => setDatasetMode(true)}
          />

          {/* Versions section */}
          <div className="def-section" style={{ marginBottom: 16 }}>
            <div className="def-section-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3>Versions <span style={{ fontSize: 12, color: '#94a3b8', fontWeight: 400 }}>newest first</span></h3>
              <button className="btn-accent" onClick={openNewVersionForm}>+ New Version</button>
            </div>

            {versions.length === 0 ? (
              <div className="node-hub-empty">No versions yet. Click "+ New Version" to create one.</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {versions.map((v) => (
                  <VersionCard
                    key={v.id}
                    version={v}
                    expanded={expandedVersionId === v.id}
                    editing={editingVersionId === v.id}
                    revisions={revisionsByVersion[v.id]}
                    expandedRevisionId={expandedRevisionId}
                    confirmDelete={confirmDelete}
                    onToggle={() => {
                      if (expandedVersionId === v.id) {
                        setExpandedVersionId(null);
                        setEditingVersionId(null);
                      } else {
                        setExpandedVersionId(v.id);
                        if (!revisionsByVersion[v.id]) fetchRevisions(v.id);
                      }
                    }}
                    onEdit={() => beginEditVersion(v.id)}
                    onCancelEdit={() => setEditingVersionId(null)}
                    onSaveEdit={(patch) => updateVersion(v.id, patch)}
                    onRequestDelete={() => setConfirmDelete(`version:${v.id}`)}
                    onConfirmDelete={() => deleteVersion(v.id)}
                    onCancelDelete={() => setConfirmDelete(null)}
                    onCreateRevision={() => createRevision(v.id)}
                    onExpandRevision={(rid) => setExpandedRevisionId(expandedRevisionId === rid ? null : rid)}
                    onUpdateRevision={updateRevision}
                    onRequestDeleteRevision={(rid) => setConfirmDelete(`revision:${rid}`)}
                    onConfirmDeleteRevision={(rid) => deleteRevision(v.id, rid)}
                    onOpenCanvasViewer={(rev) => openCanvasViewer(v, rev)}
                  />
                ))}
              </div>
            )}
          </div>
        </div>

        {/* New Version modal (create only; edits are inline) */}
        {showVersionForm && (
          <div className="modal-overlay" onClick={() => setShowVersionForm(false)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <h3>New Version</h3>
              <form onSubmit={createVersionFromForm}>
                <div className="form-group">
                  <label><span className={versionForm.name.trim() ? 'valid-star' : 'required-star'}>*</span> Name</label>
                  <input
                    value={versionForm.name}
                    onChange={(e) => setVersionForm({ ...versionForm, name: e.target.value })}
                    placeholder="e.g. Baseline 2026, Net-Zero scenario"
                  />
                </div>
                <div className="form-group">
                  <label>Description</label>
                  <textarea
                    value={versionForm.description}
                    onChange={(e) => setVersionForm({ ...versionForm, description: e.target.value })}
                    placeholder="What's in this version? Key assumptions, scope…"
                    rows={3}
                  />
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Version Date</label>
                    <input
                      type="date"
                      value={versionForm.version_date}
                      onChange={(e) => setVersionForm({ ...versionForm, version_date: e.target.value })}
                    />
                  </div>
                  <div className="form-group">
                    <label>Status</label>
                    <select
                      value={versionForm.status}
                      onChange={(e) => setVersionForm({ ...versionForm, status: e.target.value })}
                    >
                      {VERSION_STATUS_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                </div>
                <div className="form-actions">
                  <button type="button" className="btn-secondary" onClick={() => setShowVersionForm(false)}>Cancel</button>
                  <button type="submit" className="btn-primary" disabled={!versionForm.name.trim()}>
                    Create Version
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    );
  }

  // ========================================================================
  // Projects list
  // ========================================================================
  return (
    <div className="def-list-view">
      <div className="def-list-header">
        <div>
          <h1>Projects</h1>
          <p className="def-list-subtitle">
            Energy modelling projects. Each project has versions, each version has up to 4 revisions for comparison.
          </p>
        </div>
        <button className="btn-accent" onClick={handleNewProject}>+ New Project</button>
      </div>

      {loading ? (
        <div className="node-hub-empty">Loading...</div>
      ) : projects.length === 0 ? (
        <div className="node-hub-empty">No projects yet. Click "+ New Project" to create one.</div>
      ) : (
        <div className="def-card-grid" style={{ padding: 16 }}>
          {projects.map((proj) => (
            <div
              key={proj.id}
              className="def-card"
              style={{ borderColor: STATUS_COLORS[proj.status] || '#4ecca3', cursor: 'pointer' }}
              onClick={() => fetchProjectDetail(proj.id)}
            >
              <div className="def-card-info">
                <div className="def-card-name">
                  <span className="location-code-badge">{proj.project_code}</span>
                  {proj.name}
                </div>
                <div className="def-card-meta">
                  <span style={{ color: STATUS_COLORS[proj.status] }}>{proj.status}</span>
                  <span>{proj.version_count || 0} versions</span>
                  {proj.location_name && <span>{proj.location_code} {proj.location_name}</span>}
                  {proj.client && <span>{proj.client}</span>}
                </div>
                {proj.description && <div className="def-card-dims">{proj.description}</div>}
              </div>
              <div className="def-card-actions">
                <button className="btn-card" onClick={(e) => { e.stopPropagation(); fetchProjectDetail(proj.id); }}>Open</button>
                <button className="btn-card" onClick={(e) => { e.stopPropagation(); handleEdit(proj); }}>Edit</button>
                <button className="btn-card danger" onClick={(e) => { e.stopPropagation(); handleDelete(proj.id); }}>Delete</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Project form modal */}
      {showForm && (
        <div className="modal-overlay" onClick={() => setShowForm(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>{editingId ? 'Edit Project' : 'New Project'}</h3>
            <form onSubmit={handleSave}>
              <div className="form-group">
                <label><span className={formData.project_code.length === 5 ? 'valid-star' : 'required-star'}>*</span> Project Code (5 chars)</label>
                <input name="project_code" value={formData.project_code} onChange={handleFormChange} placeholder="PPR01" maxLength={5} className={formData.project_code.length === 5 ? 'field-valid' : formData.project_code ? 'field-invalid' : ''} />
              </div>
              <div className="form-group">
                <label><span className={formData.name.trim() ? 'valid-star' : 'required-star'}>*</span> Name</label>
                <input name="name" value={formData.name} onChange={handleFormChange} placeholder="e.g. Prieska Power Reserve" />
              </div>
              <div className="form-group">
                <label>Description</label>
                <input name="description" value={formData.description} onChange={handleFormChange} placeholder="Project description" />
              </div>
              <div className="form-group">
                <label>Client</label>
                <input name="client" value={formData.client} onChange={handleFormChange} placeholder="Client name" />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Location</label>
                  <select name="location_id" value={formData.location_id} onChange={handleFormChange}>
                    <option value="">-- None --</option>
                    {locations.map((l) => (
                      <option key={l.id} value={l.id}>{l.location_code} {'—'} {l.name}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Status</label>
                  <select name="status" value={formData.status} onChange={handleFormChange}>
                    <option value="active">Active</option>
                    <option value="draft">Draft</option>
                    <option value="archived">Archived</option>
                  </select>
                </div>
              </div>
              <div className="form-actions">
                <button type="button" className="btn-secondary" onClick={() => setShowForm(false)}>Cancel</button>
                <button type="submit" className="btn-primary" disabled={formData.project_code.length !== 5 || !formData.name.trim()}>
                  {editingId ? 'Update' : 'Create'} Project
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

// ===========================================================================
// GenericItemSummary — reusable compact bar for Nodes / Lines
// ===========================================================================
const GenericItemSummary = ({ label, items, onConfigure }) => {
  const count = items.length;
  const statusColor = count > 0 ? '#4ecca3' : '#94a3b8';
  const byCategory = {};
  items.forEach((a) => { byCategory[a.category] = (byCategory[a.category] || 0) + 1; });

  return (
    <div style={{
      background: '#1e293b',
      border: '1px solid #334155',
      borderLeft: `4px solid ${statusColor}`,
      borderRadius: 6,
      marginBottom: 12,
      padding: '10px 16px',
      display: 'flex',
      alignItems: 'center',
      gap: 12,
      flexWrap: 'wrap',
    }}>
      <div style={{ fontSize: 14, fontWeight: 600, color: '#e2e8f0', fontFamily: "'Space Grotesk', sans-serif" }}>
        <span style={{ color: statusColor, marginRight: 8 }}>{count > 0 ? '●' : '○'}</span>{label}
      </div>
      <div style={{ fontSize: 11, color: '#94a3b8', fontFamily: "'JetBrains Mono', monospace" }}>
        {count > 0 ? `${count} configured` : 'Not configured yet'}
      </div>
      {Object.keys(byCategory).length > 0 && (
        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
          {Object.entries(byCategory).map(([cat, n]) => (
            <span key={cat} style={{
              background: '#334155', color: '#e2e8f0', padding: '2px 8px', borderRadius: 3,
              fontSize: 10, fontWeight: 600, fontFamily: "'JetBrains Mono', monospace",
            }}>
              {cat} · {n}
            </span>
          ))}
        </div>
      )}
      <div style={{ flex: 1 }} />
      <button className="btn-accent" onClick={onConfigure}>Configure →</button>
    </div>
  );
};

// ===========================================================================
// GenericItemConfigScreen — reusable sub-screen for Nodes / Lines
// ===========================================================================
const NODE_CATEGORIES = [
  { id: 'electric', label: 'Electric', color: '#D4A843' },
  { id: 'gas', label: 'Gas', color: '#3CC0C9' },
  { id: 'liquid', label: 'Liquid', color: '#7B68AE' },
];

const GenericItemConfigScreen = ({ project, items, definitions, itemType, itemLabel, defIdField, onCreate, onUpdate, onDelete, onBack }) => {
  const [selectedId, setSelectedId] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const selectedItem = items.find((a) => a.id === selectedId);
  const selectedDef = selectedItem ? definitions.find((d) => d.id === selectedItem[defIdField]) : null;

  const grouped = {};
  items.forEach((a) => { (grouped[a.category] = grouped[a.category] || []).push(a); });

  const handleAdd = async (defId) => {
    const def = definitions.find((d) => d.id === defId);
    if (!def) return;
    const created = await onCreate(defId, def.name);
    setShowAddModal(false);
    if (created) setSelectedId(created.id);
  };

  const handleDelete = async (aid) => {
    await onDelete(aid);
    setConfirmDelete(null);
    if (selectedId === aid) setSelectedId(null);
  };

  return (
    <div className="def-list-view">
      <div className="def-list-header">
        <div>
          <h1>
            <span className="location-code-badge" style={{ fontSize: 12 }}>{project.project_code}</span>
            {' '}{project.name}
            <span style={{ fontSize: 14, color: '#94a3b8', fontWeight: 400, marginLeft: 10 }}>/ {itemLabel}</span>
          </h1>
          <p className="def-list-subtitle">{project.description} · {items.length} {itemType}{items.length === 1 ? '' : 's'}</p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn-ghost" onClick={onBack}>{'← Back to Project'}</button>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 12, padding: '0 16px 24px 16px', alignItems: 'flex-start' }}>
        {/* Left list */}
        <div style={{
          width: 220, minWidth: 220, background: '#1e293b', border: '1px solid #334155',
          borderRadius: 6, padding: 8, maxHeight: 'calc(100vh - 160px)', overflowY: 'auto',
        }}>
          <button className="btn-accent" style={{ width: '100%', marginBottom: 8, fontSize: 11, padding: '6px 0' }} onClick={() => setShowAddModal(true)}>
            + Add {itemType === 'node' ? 'Node' : 'Line'}
          </button>
          {items.length === 0 && (
            <div style={{ color: '#94a3b8', fontSize: 11, textAlign: 'center', padding: '16px 0', fontStyle: 'italic' }}>
              No {itemLabel.toLowerCase()} yet.
            </div>
          )}
          {NODE_CATEGORIES.map((cat) => {
            const catItems = grouped[cat.id] || [];
            if (catItems.length === 0) return null;
            return (
              <div key={cat.id} style={{ marginBottom: 8 }}>
                <div style={{ padding: '4px 6px 2px 6px', fontSize: 9, fontWeight: 600, color: cat.color, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  <span style={{ display: 'inline-block', width: 5, height: 5, borderRadius: '50%', background: cat.color, marginRight: 4 }} />
                  {cat.label}
                </div>
                {catItems.map((item) => {
                  const isSelected = item.id === selectedId;
                  return (
                    <button
                      key={item.id}
                      onClick={() => setSelectedId(item.id)}
                      onMouseEnter={(e) => { if (!isSelected) { e.currentTarget.style.background = '#1e2a4a'; e.currentTarget.style.color = cat.color; e.currentTarget.style.borderLeftColor = cat.color; } }}
                      onMouseLeave={(e) => { if (!isSelected) { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#e2e8f0'; e.currentTarget.style.borderLeftColor = 'transparent'; } }}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 6, width: '100%', textAlign: 'left',
                        padding: '4px 6px', marginBottom: 1, background: isSelected ? '#1e2a4a' : 'transparent',
                        color: isSelected ? '#4ecca3' : '#e2e8f0', border: 'none',
                        borderLeft: `2px solid ${isSelected ? '#4ecca3' : 'transparent'}`, borderRadius: 2,
                        cursor: 'pointer', fontSize: 11, fontFamily: 'inherit', fontWeight: isSelected ? 600 : 400,
                        transition: 'background 0.12s, color 0.12s, border-left-color 0.12s',
                      }}
                    >
                      <span style={{ fontSize: 13 }}>{item.icon || (itemType === 'line' ? '—' : '◎')}</span>
                      <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.name}</span>
                    </button>
                  );
                })}
              </div>
            );
          })}
        </div>

        {/* Right config panel */}
        <div style={{ flex: 1, minWidth: 0 }}>
          {selectedItem ? (
            <GenericItemPanel
              item={selectedItem}
              definition={selectedDef}
              itemType={itemType}
              defIdField={defIdField}
              confirmDelete={confirmDelete}
              onUpdate={(patch) => onUpdate(selectedItem.id, patch)}
              onRequestDelete={() => setConfirmDelete(selectedItem.id)}
              onConfirmDelete={() => handleDelete(selectedItem.id)}
              onCancelDelete={() => setConfirmDelete(null)}
            />
          ) : (
            <div style={{ background: '#1e293b', border: '1px solid #334155', borderRadius: 6, padding: 60, textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>
              Select a {itemType} from the list, or click "+ Add" to add a new one.
            </div>
          )}
        </div>
      </div>

      {/* Add modal */}
      {showAddModal && (
        <div className="modal-overlay" onClick={() => setShowAddModal(false)}>
          <div className="modal-content" style={{ maxWidth: 720 }} onClick={(e) => e.stopPropagation()}>
            <h3>Add {itemType === 'node' ? 'Node' : 'Line'}</h3>
            <p style={{ fontSize: 12, color: '#94a3b8', marginTop: -8, marginBottom: 16 }}>
              Pick a definition from the library.
            </p>
            {NODE_CATEGORIES.map((cat) => {
              const catDefs = definitions.filter((d) => d.category === cat.id);
              if (catDefs.length === 0) return null;
              return (
                <div key={cat.id} style={{ marginBottom: 16 }}>
                  <div style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.5px', color: '#94a3b8', marginBottom: 8 }}>
                    <span style={{ display: 'inline-block', width: 6, height: 6, borderRadius: '50%', background: cat.color, marginRight: 4 }} />
                    {cat.label}
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 8 }}>
                    {catDefs.map((d) => (
                      <button key={d.id} onClick={() => handleAdd(d.id)}
                        style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', background: '#1e293b', border: `1px solid #334155`, borderLeft: `3px solid ${cat.color}`, borderRadius: 4, color: '#e2e8f0', cursor: 'pointer', fontFamily: 'inherit', fontSize: 12, textAlign: 'left' }}
                        onMouseEnter={(e) => { e.currentTarget.style.background = '#1e2a4a'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.background = '#1e293b'; }}
                      >
                        <span style={{ fontSize: 16 }}>{d.icon || '◎'}</span>
                        <span style={{ flex: 1 }}>{d.name}</span>
                      </button>
                    ))}
                  </div>
                </div>
              );
            })}
            <div className="form-actions">
              <button type="button" className="btn-secondary" onClick={() => setShowAddModal(false)}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ===========================================================================
// GenericItemPanel — right-side config for a node or line instance
// ===========================================================================
const GenericItemPanel = ({ item, definition, itemType, defIdField, confirmDelete, onUpdate, onRequestDelete, onConfirmDelete, onCancelDelete }) => {
  const [name, setName] = useState(item.name);
  const [config, setConfig] = useState(item.config || {});
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    setName(item.name);
    setConfig(item.config || {});
    setDirty(false);
  }, [item.id, item.updated_at]);

  const defBody = (definition && definition.definition && typeof definition.definition === 'object') ? definition.definition : definition || {};
  const fields = (defBody.fields || definition?.fields || []).filter((f) => f.name !== 'status');

  const handleField = (fieldName, value) => { setConfig((prev) => ({ ...prev, [fieldName]: value })); setDirty(true); };
  const save = () => { onUpdate({ name, config }); setDirty(false); };
  const isConfirming = confirmDelete === item.id;

  const catMeta = NODE_CATEGORIES.find((c) => c.id === item.category);
  const catColor = catMeta?.color || '#94a3b8';

  const renderField = (field) => {
    const val = config[field.name] ?? field.defaultValue ?? '';
    switch (field.type) {
      case 'number':
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <input type="number" value={val} onChange={(e) => handleField(field.name, e.target.value)} style={{ flex: 1 }} />
            {field.unit && <span style={{ fontSize: 12, color: '#94a3b8' }}>{field.unit}</span>}
          </div>
        );
      case 'select':
        return (
          <select value={val} onChange={(e) => handleField(field.name, e.target.value)} style={{ width: '100%' }}>
            {!val && <option value="">— Select —</option>}
            {(field.options || []).map((opt) => <option key={opt} value={opt}>{opt}</option>)}
          </select>
        );
      default:
        return <input type="text" value={val} onChange={(e) => handleField(field.name, e.target.value)} style={{ width: '100%' }} />;
    }
  };

  // Extra read-only info for lines
  const isLine = itemType === 'line';
  const lineInfo = isLine ? {
    voltage: item.voltage_kv, conductor: item.conductor, lineType: item.line_type,
    r: item.r_ohm_per_km, x: item.x_ohm_per_km, sMax: item.s_max_mva, iThermal: item.i_thermal_a,
  } : null;

  return (
    <div style={{ background: '#1e293b', border: '1px solid #334155', borderLeft: `4px solid ${catColor}`, borderRadius: 6, padding: 16 }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 10 }}>
        <div style={{ fontSize: 24, lineHeight: 1 }}>{item.icon || (isLine ? '⚡' : '◎')}</div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <input type="text" value={name} onChange={(e) => { setName(e.target.value); setDirty(true); }}
            style={{ fontSize: 18, fontWeight: 600, maxWidth: 300, background: 'transparent', border: 'none', borderBottom: '1px dashed #334155', color: '#e2e8f0', fontFamily: "'Space Grotesk', sans-serif", padding: '2px 0' }}
          />
          <div style={{ marginTop: 4, display: 'flex', gap: 8, alignItems: 'center', fontSize: 11, fontFamily: "'JetBrains Mono', monospace" }}>
            <span style={{ background: catColor, color: '#0f172a', padding: '2px 8px', borderRadius: 3, fontWeight: 700 }}>
              {catMeta?.label || item.category}
            </span>
            <span style={{ color: '#94a3b8' }}>{item.definition_name || item[defIdField]}</span>
          </div>
        </div>
      </div>

      {/* Line-specific read-only electrical params */}
      {isLine && lineInfo && (
        <div style={{
          background: '#0f172a', border: '1px solid #334155', borderRadius: 4, padding: 10, marginBottom: 12,
          display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: '4px 16px',
          fontSize: 11, color: '#94a3b8', fontFamily: "'JetBrains Mono', monospace",
        }}>
          {lineInfo.lineType && <div><span style={{ color: '#e2e8f0' }}>{lineInfo.lineType}</span></div>}
          {lineInfo.voltage && <div>Voltage: <span style={{ color: '#e2e8f0' }}>{lineInfo.voltage} kV</span></div>}
          {lineInfo.conductor && <div>Conductor: <span style={{ color: '#e2e8f0' }}>{lineInfo.conductor}</span></div>}
          {lineInfo.r && <div>R: <span style={{ color: '#e2e8f0' }}>{lineInfo.r} Ω/km</span></div>}
          {lineInfo.x && <div>X: <span style={{ color: '#e2e8f0' }}>{lineInfo.x} Ω/km</span></div>}
          {lineInfo.sMax && <div>S max: <span style={{ color: '#e2e8f0' }}>{lineInfo.sMax} MVA</span></div>}
          {lineInfo.iThermal && <div>I rated: <span style={{ color: '#e2e8f0' }}>{lineInfo.iThermal} A</span></div>}
        </div>
      )}

      {/* Fields grid */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 10, marginBottom: 12 }}>
        {fields.map((f) => (
          <div key={f.id || f.name} className="form-group" style={{ marginBottom: 0 }}>
            <label>{f.label || f.name}{f.required && <span style={{ color: '#dc2626', marginLeft: 4 }}>*</span>}</label>
            {renderField(f)}
          </div>
        ))}
      </div>

      {/* Actions */}
      <div style={{ display: 'flex', gap: 8, marginTop: 12, alignItems: 'center' }}>
        <button className="btn-accent" onClick={save} disabled={!dirty}>
          {dirty ? 'Save changes' : 'Saved'}
        </button>
        <div style={{ flex: 1 }} />
        {isConfirming ? (
          <>
            <button className="btn-card danger" onClick={onConfirmDelete}>Confirm delete</button>
            <button className="btn-card" onClick={onCancelDelete}>Cancel</button>
          </>
        ) : (
          <button className="btn-card danger" onClick={onRequestDelete}>Delete {itemType}</button>
        )}
      </div>
    </div>
  );
};

// ===========================================================================
// ClimateDataSummary — compact status bar on project detail view
// ===========================================================================
const ClimateDataSummary = ({ project, sources, onConfigure }) => {
  const byVar = {};
  sources.forEach((s) => { byVar[s.variable] = s; });
  const requiredVars = CLIMATE_VARIABLES.filter((v) => v.required);
  const missingRequired = requiredVars.filter((v) => !byVar[v.id]);
  const totalSet = sources.length;

  // Unique source names across all assignments (for badge strip)
  const uniqueSources = Array.from(new Set(sources.map((s) => s.source)));

  const hasLocation = !!project.location_id;
  const statusColor = !hasLocation
    ? '#fbbf24'
    : missingRequired.length === 0
      ? '#4ecca3'
      : '#dc2626';
  const statusText = !hasLocation
    ? 'No project location set'
    : missingRequired.length === 0
      ? `All required variables configured (${totalSet}/${CLIMATE_VARIABLES.length} set)`
      : `Missing: ${missingRequired.map((v) => v.label).join(', ')}`;

  return (
    <div style={{
      background: '#1e293b',
      border: '1px solid #334155',
      borderLeft: `4px solid ${statusColor}`,
      borderRadius: 6,
      marginBottom: 16,
      padding: '12px 16px',
      display: 'flex',
      alignItems: 'center',
      gap: 12,
      flexWrap: 'wrap',
    }}>
      <div style={{
        fontSize: 15,
        fontWeight: 600,
        color: '#e2e8f0',
        fontFamily: "'Space Grotesk', sans-serif",
      }}>
        <span style={{ color: statusColor, marginRight: 8 }}>●</span>
        Climate Data
      </div>
      <div style={{ fontSize: 12, color: '#94a3b8', fontFamily: "'JetBrains Mono', monospace" }}>
        {statusText}
      </div>
      {uniqueSources.length > 0 && (
        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
          {uniqueSources.map((src) => (
            <span
              key={src}
              style={{
                background: sourceColor(src),
                color: '#0f172a',
                padding: '2px 8px',
                borderRadius: 3,
                fontSize: 10,
                fontWeight: 700,
                fontFamily: "'JetBrains Mono', monospace",
              }}
            >
              {src}
            </span>
          ))}
        </div>
      )}
      <div style={{ flex: 1 }} />
      <button className="btn-accent" onClick={onConfigure} disabled={!hasLocation}>
        Configure Sources →
      </button>
    </div>
  );
};

// ===========================================================================
// ClimateConfigScreen — full-view sub-screen with map + variable selection
// ===========================================================================
const ClimateConfigScreen = ({ project, location, sources, candidates, availableSources, onSave, onBack }) => {
  // Build initial mapping from current sources
  const initial = {};
  CLIMATE_VARIABLES.forEach((v) => {
    const existing = sources.find((s) => s.variable === v.id);
    initial[v.id] = existing ? String(existing.dataset_id) : '';
  });
  const [mapping, setMapping] = useState(initial);

  const handleChange = (varId, value) => {
    setMapping((prev) => ({ ...prev, [varId]: value }));
  };

  const submit = () => {
    const payload = {};
    CLIMATE_VARIABLES.forEach((v) => {
      const val = mapping[v.id];
      payload[v.id] = val ? Number(val) : null;
    });
    onSave(payload);
  };

  const missingRequired = CLIMATE_VARIABLES
    .filter((v) => v.required)
    .filter((v) => !mapping[v.id]);
  const statusColor = missingRequired.length === 0 ? '#4ecca3' : '#dc2626';
  const statusText = missingRequired.length === 0
    ? 'All required variables configured'
    : `Missing: ${missingRequired.map((v) => v.label).join(', ')}`;

  return (
    <div className="def-list-view">
      <div className="def-list-header">
        <div>
          <h1>
            <span style={{ color: statusColor, marginRight: 8 }}>●</span>
            Climate Sources
          </h1>
          <p className="def-list-subtitle">
            {project.name}
            {location && (
              <> · {location.location_code} {location.name} · {location.latitude?.toFixed(4)}, {location.longitude?.toFixed(4)}</>
            )}
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn-ghost" onClick={onBack}>{'← Back'}</button>
          <button className="btn-accent" onClick={submit}>Save Sources</button>
        </div>
      </div>

      {/* Map (top) */}
      <div style={{ padding: '0 16px', marginBottom: 16 }}>
        <div style={{
          border: '1px solid #334155',
          borderRadius: 6,
          overflow: 'hidden',
          height: 420,
          position: 'relative',
        }}>
          {location && location.latitude != null && location.longitude != null ? (
            <ClimateMap
              location={{
                latitude: location.latitude,
                longitude: location.longitude,
                name: location.name,
                elevation_m: location.elevation_m,
              }}
              sources={availableSources}
              selectedSources={availableSources.map((s) => s.id)}
              onMapClick={() => {}}
              onSourceSelect={() => {}}
              pickMode={false}
            />
          ) : (
            <div style={{ padding: 40, color: '#94a3b8', textAlign: 'center' }}>
              Location has no coordinates set.
            </div>
          )}
        </div>
        {availableSources.length === 0 && location && (
          <div style={{ marginTop: 8, fontSize: 12, color: '#fbbf24' }}>
            No climate datasets downloaded at this location. Open Climate Data in the main menu and download data for {location.location_code}.
          </div>
        )}
      </div>

      {/* Variable selection (bottom) */}
      <div style={{ padding: '0 16px 24px 16px' }}>
        <div style={{
          background: '#1e293b',
          border: '1px solid #334155',
          borderRadius: 6,
          padding: 16,
        }}>
          <div style={{ fontSize: 12, color: '#94a3b8', marginBottom: 12, fontFamily: "'JetBrains Mono', monospace" }}>
            {statusText}
          </div>

          {['required', 'solar'].map((group) => (
            <div key={group} style={{ marginBottom: 16 }}>
              <div style={{
                fontSize: 10,
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                color: '#94a3b8',
                marginBottom: 8,
              }}>
                {group === 'required' ? 'Required' : 'Optional — Solar PV'}
              </div>
              {CLIMATE_VARIABLES.filter((v) => v.group === group).map((v) => {
                const opts = candidates[v.id] || [];
                const hasOptions = opts.length > 0;
                return (
                  <div key={v.id} className="form-group" style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 8 }}>
                    <label style={{ width: 180, marginBottom: 0 }}>
                      {v.label}
                      {v.required && <span style={{ color: '#dc2626', marginLeft: 4 }}>*</span>}
                      {v.note && <div style={{ fontSize: 10, color: '#94a3b8', fontWeight: 400 }}>{v.note}</div>}
                    </label>
                    {hasOptions ? (
                      <select
                        value={mapping[v.id]}
                        onChange={(e) => handleChange(v.id, e.target.value)}
                        style={{ flex: 1, maxWidth: 520 }}
                      >
                        <option value="">— None —</option>
                        {opts.map((d) => (
                          <option key={d.id} value={d.id}>
                            {d.source} · {d.year_start}–{d.year_end}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <div style={{ flex: 1, color: '#dc2626', fontSize: 12, fontStyle: 'italic' }}>
                        No dataset at this location contains {v.label.toLowerCase()}.
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// ===========================================================================
// AssetsSummary — compact status bar on project detail view
// ===========================================================================
const AssetsSummary = ({ project, assets, onConfigure }) => {
  const byCategory = {};
  assets.forEach((a) => {
    byCategory[a.category] = (byCategory[a.category] || 0) + 1;
  });

  // Simple consistency: green if ≥1 asset, yellow if none (neutral, not an error)
  const statusColor = assets.length > 0 ? '#4ecca3' : '#fbbf24';
  const statusText = assets.length > 0
    ? `${assets.length} asset${assets.length === 1 ? '' : 's'} configured`
    : 'No assets yet';

  return (
    <div style={{
      background: '#1e293b',
      border: '1px solid #334155',
      borderLeft: `4px solid ${statusColor}`,
      borderRadius: 6,
      marginBottom: 16,
      padding: '12px 16px',
      display: 'flex',
      alignItems: 'center',
      gap: 12,
      flexWrap: 'wrap',
    }}>
      <div style={{
        fontSize: 15,
        fontWeight: 600,
        color: '#e2e8f0',
        fontFamily: "'Space Grotesk', sans-serif",
      }}>
        <span style={{ color: statusColor, marginRight: 8 }}>●</span>
        Assets
      </div>
      <div style={{ fontSize: 12, color: '#94a3b8', fontFamily: "'JetBrains Mono', monospace" }}>
        {statusText}
      </div>
      {Object.keys(byCategory).length > 0 && (
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {Object.entries(byCategory).map(([cat, n]) => {
            const c = CATEGORIES.find((x) => x.id === cat);
            const color = c?.color || '#94a3b8';
            return (
              <span
                key={cat}
                style={{
                  background: color,
                  color: '#0f172a',
                  padding: '2px 8px',
                  borderRadius: 3,
                  fontSize: 10,
                  fontWeight: 700,
                  fontFamily: "'JetBrains Mono', monospace",
                }}
              >
                {c?.label || cat} · {n}
              </span>
            );
          })}
        </div>
      )}
      <div style={{ flex: 1 }} />
      <button className="btn-accent" onClick={onConfigure}>
        Configure →
      </button>
    </div>
  );
};

// ===========================================================================
// AssetsConfigScreen — left list + right config panel, with Add modal
// ===========================================================================
const AssetsConfigScreen = ({ project, assets, definitions, onCreate, onUpdate, onDelete, onBack }) => {
  const [selectedId, setSelectedId] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [lineVoltages, setLineVoltages] = useState([]);

  // Fetch line voltage levels once for the whole screen
  useEffect(() => {
    fetch(`${API}/api/line-definitions/voltage-levels`)
      .then((r) => r.ok ? r.json() : [])
      .then((levels) => setLineVoltages(levels || []))
      .catch(() => {});
  }, []);

  const selectedAsset = assets.find((a) => a.id === selectedId);
  const selectedDef = selectedAsset ? definitions.find((d) => d.id === selectedAsset.asset_def_id) : null;

  // Group assets by category
  const groupedAssets = {};
  assets.forEach((a) => {
    (groupedAssets[a.category] = groupedAssets[a.category] || []).push(a);
  });

  const handleAdd = async (defId) => {
    const def = definitions.find((d) => d.id === defId);
    if (!def) return;
    const created = await onCreate(defId, def.name);
    setShowAddModal(false);
    if (created) setSelectedId(created.id);
  };

  const handleDelete = async (aid) => {
    await onDelete(aid);
    setConfirmDelete(null);
    if (selectedId === aid) setSelectedId(null);
  };

  return (
    <div className="def-list-view">
      {/* Project context header */}
      <div className="def-list-header">
        <div>
          <h1>
            <span className="location-code-badge" style={{ fontSize: 12 }}>{project.project_code}</span>
            {' '}{project.name}
            <span style={{ fontSize: 14, color: '#94a3b8', fontWeight: 400, marginLeft: 10 }}>/ Assets</span>
          </h1>
          <p className="def-list-subtitle">{project.description} · {assets.length} asset{assets.length === 1 ? '' : 's'}</p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn-ghost" onClick={onBack}>{'← Back to Project'}</button>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 12, padding: '0 16px 24px 16px', alignItems: 'flex-start' }}>
        {/* Left column — asset list */}
        <div style={{
          width: 220,
          minWidth: 220,
          background: '#1e293b',
          border: '1px solid #334155',
          borderRadius: 6,
          padding: 8,
          maxHeight: 'calc(100vh - 160px)',
          overflowY: 'auto',
        }}>
          <button
            className="btn-accent"
            style={{ width: '100%', marginBottom: 8, fontSize: 11, padding: '6px 0' }}
            onClick={() => setShowAddModal(true)}
          >
            + Add Asset
          </button>

          {assets.length === 0 && (
            <div style={{ color: '#94a3b8', fontSize: 12, textAlign: 'center', padding: '20px 0' }}>
              No assets yet. Click "+ Add Asset" to add one.
            </div>
          )}

          {CATEGORIES.map((cat) => {
            const items = groupedAssets[cat.id] || [];
            if (items.length === 0) return null;
            return (
              <div key={cat.id} style={{ marginBottom: 8 }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 4,
                  fontSize: 9,
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  color: cat.color,
                  fontWeight: 600,
                  padding: '4px 6px 2px 6px',
                }}>
                  <span style={{ width: 5, height: 5, borderRadius: '50%', background: cat.color }} />
                  {cat.label}
                </div>
                {items.map((a) => {
                  const isSelected = a.id === selectedId;
                  return (
                    <button
                      key={a.id}
                      onClick={() => setSelectedId(a.id)}
                      onMouseEnter={(e) => {
                        if (!isSelected) {
                          e.currentTarget.style.background = '#1e2a4a';
                          e.currentTarget.style.color = cat.color;
                          e.currentTarget.style.borderLeftColor = cat.color;
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!isSelected) {
                          e.currentTarget.style.background = 'transparent';
                          e.currentTarget.style.color = '#e2e8f0';
                          e.currentTarget.style.borderLeftColor = 'transparent';
                        }
                      }}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 6,
                        width: '100%',
                        textAlign: 'left',
                        padding: '4px 6px',
                        marginBottom: 1,
                        background: isSelected ? '#1e2a4a' : 'transparent',
                        color: isSelected ? '#4ecca3' : '#e2e8f0',
                        border: 'none',
                        borderLeft: `2px solid ${isSelected ? '#4ecca3' : 'transparent'}`,
                        borderRadius: 2,
                        cursor: 'pointer',
                        fontSize: 11,
                        fontFamily: 'inherit',
                        fontWeight: isSelected ? 600 : 400,
                        transition: 'background 0.12s, color 0.12s, border-left-color 0.12s',
                      }}
                    >
                      <span style={{ fontSize: 13 }}>{a.icon || '▢'}</span>
                      <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {a.name}
                      </span>
                    </button>
                  );
                })}
              </div>
            );
          })}
        </div>

        {/* Right column — config panel */}
        <div style={{ flex: 1, minWidth: 0 }}>
          {selectedAsset ? (
            <AssetConfigPanel
              asset={selectedAsset}
              definition={selectedDef}
              confirmDelete={confirmDelete}
              lineVoltages={lineVoltages}
              onUpdate={(patch) => onUpdate(selectedAsset.id, patch)}
              onRequestDelete={() => setConfirmDelete(selectedAsset.id)}
              onConfirmDelete={() => handleDelete(selectedAsset.id)}
              onCancelDelete={() => setConfirmDelete(null)}
            />
          ) : (
            <div style={{
              background: '#1e293b',
              border: '1px solid #334155',
              borderRadius: 6,
              padding: 60,
              textAlign: 'center',
              color: '#94a3b8',
              fontSize: 13,
            }}>
              Select an asset from the list, or click "+ Add Asset" to add a new one.
            </div>
          )}
        </div>
      </div>

      {/* Add Asset modal */}
      {showAddModal && (
        <AssetDefinitionPicker
          definitions={definitions}
          onPick={handleAdd}
          onClose={() => setShowAddModal(false)}
        />
      )}
    </div>
  );
};

// ===========================================================================
// AssetConfigPanel — right-side per-asset editor (schema-aware)
// ===========================================================================
const AssetConfigPanel = ({ asset, definition, confirmDelete, lineVoltages, onUpdate, onRequestDelete, onConfirmDelete, onCancelDelete }) => {
  const [name, setName] = useState(asset.name);
  const [config, setConfig] = useState(asset.config || {});
  const [dirty, setDirty] = useState(false);
  const [showConnectorEditor, setShowConnectorEditor] = useState(false);

  // Use line voltages passed from parent
  const nodeVoltages = lineVoltages || [];

  useEffect(() => {
    setName(asset.name);
    setConfig(asset.config || {});
    setDirty(false);
  }, [asset.id, asset.updated_at]);

  // Unwrap both JSONB shapes: flat vs double-wrapped
  const defBody = (definition && definition.definition && typeof definition.definition === 'object')
    ? definition.definition
    : definition || {};
  const fields = (defBody.fields || definition?.fields || []).filter((f) => f.name !== 'status');

  const handleField = (fieldName, value) => {
    setConfig((prev) => ({ ...prev, [fieldName]: value }));
    setDirty(true);
  };

  const save = () => {
    onUpdate({ name, config });
    setDirty(false);
  };

  const [showFuturePopup, setShowFuturePopup] = useState(false);

  const isConfirming = confirmDelete === asset.id;
  const categoryMeta = CATEGORIES.find((c) => c.id === asset.category);
  const catColor = categoryMeta?.color || '#94a3b8';

  // Solar-specific: conditional field visibility based on timeseries_source
  const isSolar = asset.asset_def_id === 'solar';
  const tsSource = config.timeseries_source || '';
  const showSolarDetails = tsSource === 'External time series';

  const handleTimesourceChange = (value) => {
    if (value === 'PV yield calculation (future)') {
      setShowFuturePopup(true);
      return; // don't save this value
    }
    handleField('timeseries_source', value);
  };

  const renderField = (field) => {
    const val = config[field.name] ?? field.defaultValue ?? '';

    // Special handling for grid_voltage — always use line definition voltages
    if (field.name === 'grid_voltage' || field.type === 'node_voltage_ref') {
      return (
        <select value={val} onChange={(e) => handleField(field.name, e.target.value)} style={{ width: '100%' }}>
          <option value="">— Select voltage —</option>
          {nodeVoltages.map((v) => <option key={v} value={v}>{v}</option>)}
        </select>
      );
    }

    // Special handling for solar timeseries_source dropdown
    if (isSolar && field.name === 'timeseries_source') {
      return (
        <select value={val} onChange={(e) => handleTimesourceChange(e.target.value)} style={{ width: '100%' }}>
          <option value="">— None selected —</option>
          <option value="External time series">External time series</option>
          <option value="PV yield calculation (future)">PV yield calculation (future)</option>
        </select>
      );
    }

    switch (field.type) {
      case 'number':
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <input
              type="number"
              value={val}
              onChange={(e) => handleField(field.name, e.target.value)}
              style={{ flex: 1 }}
            />
            {field.unit && <span style={{ fontSize: 12, color: '#94a3b8' }}>{field.unit}</span>}
          </div>
        );
      case 'select':
        return (
          <select value={val} onChange={(e) => handleField(field.name, e.target.value)} style={{ width: '100%' }}>
            {(field.options || []).map((opt) => <option key={opt} value={opt}>{opt}</option>)}
          </select>
        );
      case 'node_voltage_ref':
        return (
          <select value={val} onChange={(e) => handleField(field.name, e.target.value)} style={{ width: '100%' }}>
            <option value="">— Select voltage —</option>
            {nodeVoltages.map((v) => <option key={v} value={v}>{v}</option>)}
          </select>
        );
      case 'boolean':
        return (
          <input
            type="checkbox"
            checked={val === true || val === 'true'}
            onChange={(e) => handleField(field.name, e.target.checked)}
          />
        );
      case 'text':
      default:
        return (
          <input
            type="text"
            value={val}
            onChange={(e) => handleField(field.name, e.target.value)}
            style={{ width: '100%' }}
          />
        );
    }
  };

  return (
    <div style={{
      background: '#1e293b',
      border: '1px solid #334155',
      borderLeft: `4px solid ${catColor}`,
      borderRadius: 6,
      padding: 20,
    }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 10 }}>
        <div style={{ fontSize: 28, lineHeight: 1 }}>{asset.icon || '▢'}</div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <input
            type="text"
            value={name}
            onChange={(e) => { setName(e.target.value); setDirty(true); }}
            style={{
              fontSize: 20,
              fontWeight: 600,
              maxWidth: 300,
              background: 'transparent',
              border: 'none',
              borderBottom: '1px dashed #334155',
              color: '#e2e8f0',
              fontFamily: "'Space Grotesk', sans-serif",
              padding: '2px 0',
            }}
          />
          <div style={{
            marginTop: 4,
            display: 'flex',
            gap: 8,
            alignItems: 'center',
            fontSize: 11,
            fontFamily: "'JetBrains Mono', monospace",
          }}>
            <span style={{
              background: catColor,
              color: '#0f172a',
              padding: '2px 8px',
              borderRadius: 3,
              fontWeight: 700,
            }}>
              {categoryMeta?.label || asset.category}
            </span>
            <span style={{ color: '#94a3b8' }}>{asset.definition_name || asset.asset_def_id}</span>
          </div>
        </div>
        {/* Connector preview — click to open editor */}
        {(() => {
          const gv = config.grid_voltage;
          const voltageValid = gv && nodeVoltages.includes(gv);
          const hasConnectorConfig = config.connectors && config.connectors.some((c) => c.enabled);
          // Green: voltage set + connectors configured. Red: voltage missing. Normal: voltage set but connectors not configured yet.
          const iconBorder = !voltageValid ? '#dc2626' : hasConnectorConfig ? '#4ecca3' : '#334155';
          const blocked = !voltageValid;
          return (
            <div
              onClick={() => {
                if (blocked) return; // silently blocked — red icon is the indicator
                setShowConnectorEditor(true);
              }}
              style={{
                cursor: blocked ? 'not-allowed' : 'pointer',
                flexShrink: 0,
                borderRadius: 4,
                border: `2px solid ${iconBorder}`,
                padding: 2,
                position: 'relative',
                opacity: blocked ? 0.7 : 1,
                transition: 'border-color 0.15s, opacity 0.15s',
              }}
              onMouseEnter={(e) => { if (!blocked) e.currentTarget.style.borderColor = catColor; }}
              onMouseLeave={(e) => { e.currentTarget.style.borderColor = iconBorder; }}
              title={blocked ? 'Select Grid Interface Voltage first' : 'Edit connector configuration'}
            >
              <ShapePreview
                width={100}
                height={100}
                frameColor={blocked ? '#dc2626' : catColor}
                connectorColor={blocked ? '#dc262644' : catColor}
                connectors={defBody.connectors || []}
                icon={asset.icon || '▢'}
                name={name}
                scale={0.45}
              />
              {/* Red cross overlay when blocked */}
              {blocked && (
                <div style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  pointerEvents: 'none',
                }}>
                  <svg width="40" height="40" viewBox="0 0 40 40" style={{ opacity: 0.8 }}>
                    <line x1="8" y1="8" x2="32" y2="32" stroke="#dc2626" strokeWidth="3" strokeLinecap="round" />
                    <line x1="32" y1="8" x2="8" y2="32" stroke="#dc2626" strokeWidth="3" strokeLinecap="round" />
                  </svg>
                </div>
              )}
            </div>
          );
        })()}
      </div>

      {/* Connector editor modal */}
      {showConnectorEditor && (
        <ConnectorEditorModal
          asset={asset}
          connectors={defBody.connectors || []}
          savedConnectors={config.connectors || null}
          frameColor={catColor}
          icon={asset.icon || '▢'}
          voltage={config.grid_voltage || ''}
          category={asset.category}
          onSave={(updatedConnectors) => {
            // Save connectors immediately to DB (don't wait for main Save button)
            const newConfig = { ...config, connectors: updatedConnectors };
            setConfig(newConfig);
            onUpdate({ name, config: newConfig });
            setShowConnectorEditor(false);
          }}
          onClose={() => setShowConnectorEditor(false)}
        />
      )}

      {/* For solar: show only the timeseries_source field first.
           All other fields + TS section only appear when "External time series" is selected. */}
      {isSolar ? (
        <>
          {/* Always show the yield data source selector */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
            gap: 12,
            marginBottom: 16,
          }}>
            {fields.filter((f) => f.name === 'timeseries_source').map((f) => (
              <div key={f.id || f.name} className="form-group" style={{ marginBottom: 0 }}>
                <label>
                  {f.label || f.name}
                  {f.required && <span style={{ color: '#dc2626', marginLeft: 4 }}>*</span>}
                </label>
                {renderField(f)}
              </div>
            ))}
          </div>

          {/* Only show remaining fields + TS section when external time series is selected */}
          {showSolarDetails && (
            <>
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
                gap: 12,
                marginBottom: 16,
              }}>
                {fields.filter((f) => f.name !== 'timeseries_source' && ['capacity', 'grid_voltage', 'gps_address'].includes(f.name)).map((f) => (
                  <div key={f.id || f.name} className="form-group" style={{ marginBottom: 0 }}>
                    <label>
                      {f.label || f.name}
                      {f.required && <span style={{ color: '#dc2626', marginLeft: 4 }}>*</span>}
                    </label>
                    {f.name === 'capacity' ? (
                      <div style={{
                        fontSize: 13,
                        fontWeight: 600,
                        color: '#e2e8f0',
                        fontFamily: "'JetBrains Mono', monospace",
                        background: '#0f172a',
                        border: '1px solid #334155',
                        borderRadius: 4,
                        padding: '8px 12px',
                      }}>
                        {config.capacity || '—'} <span style={{ color: '#94a3b8', fontWeight: 400 }}>MWac</span>
                      </div>
                    ) : renderField(f)}
                  </div>
                ))}
              </div>

              {/* Plant description blocks */}
              <SolarBlocksSection
                blocks={config.blocks || []}
                nominalCapacity={parseFloat(config.capacity) || 0}
                onChange={(blocks) => { handleField('blocks', blocks); }}
              />

              {/* Degradation */}
              <SolarDegradationSection
                degradation={config.degradation || []}
                manufacturer={config.manufacturer || ''}
                panelModel={config.panel_model || ''}
                onChange={(deg) => handleField('degradation', deg)}
              />

              {/* Time series section */}
              <div style={{ marginTop: 16 }}>
                <div style={{
                  fontSize: 11,
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  color: '#94a3b8',
                  marginBottom: 8,
                }}>
                  Time series profile
                </div>
                {asset.timeseries_id ? (
                  <TimeseriesLinkedCard
                    asset={asset}
                    onRemove={async () => {
                      if (asset.timeseries_id) {
                        await fetch(`${API}/api/timeseries/${asset.timeseries_id}`, { method: 'DELETE' });
                      }
                      onUpdate({ timeseries_id: null });
                    }}
                    onCapacityChange={(val) => handleField('capacity', val)}
                  />
                ) : (
                  <TimeseriesImporter
                    projectId={asset.project_id}
                    onImported={(created) => onUpdate({ timeseries_id: created.id })}
                  />
                )}
              </div>
            </>
          )}

          {!showSolarDetails && !tsSource && (
            <div style={{ color: '#94a3b8', fontSize: 13, fontStyle: 'italic', marginBottom: 16 }}>
              Select a yield data source above to configure this solar plant.
            </div>
          )}
        </>
      ) : (
        <>
          {/* Non-solar assets: show all fields as before */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
            gap: 12,
            marginBottom: 16,
          }}>
            {fields.map((f) => (
              <div key={f.id || f.name} className="form-group" style={{ marginBottom: 0 }}>
                <label>
                  {f.label || f.name}
                  {f.required && <span style={{ color: '#dc2626', marginLeft: 4 }}>*</span>}
                </label>
                {renderField(f)}
              </div>
            ))}
          </div>

          {/* Time series (generation / consumption / process) */}
          {['generation', 'consumption', 'process'].includes(asset.category) && (
            <div style={{ marginTop: 16 }}>
              <div style={{
                fontSize: 11,
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                color: '#94a3b8',
                marginBottom: 8,
              }}>
                Time series profile
              </div>
              {asset.timeseries_id ? (
                <TimeseriesLinkedCard
                  asset={asset}
                  onRemove={async () => {
                    if (asset.timeseries_id) {
                      await fetch(`${API}/api/timeseries/${asset.timeseries_id}`, { method: 'DELETE' });
                    }
                    onUpdate({ timeseries_id: null });
                  }}
                />
              ) : (
                <TimeseriesImporter
                  projectId={asset.project_id}
                  onImported={(created) => onUpdate({ timeseries_id: created.id })}
                />
              )}
            </div>
          )}
        </>
      )}

      {/* Future feature popup for PV yield calculation */}
      {showFuturePopup && (
        <div className="modal-overlay" onClick={() => setShowFuturePopup(false)}>
          <div className="modal-content" style={{ maxWidth: 480 }} onClick={(e) => e.stopPropagation()}>
            <h3>PV Yield Calculation</h3>
            <p style={{ color: '#94a3b8', fontSize: 13, lineHeight: 1.6 }}>
              The integrated PV yield calculation module using pvlib is a future feature currently under development.
              It will allow you to calculate solar yield directly from climate data, panel specifications,
              and array geometry — without requiring an external PVsyst or similar tool.
            </p>
            <p style={{ color: '#94a3b8', fontSize: 13 }}>
              For now, please use <strong>External time series</strong> with a pre-calculated yield file
              (e.g. from PVsyst, SAM, or other modelling software).
            </p>
            <div className="form-actions">
              <button className="btn-primary" onClick={() => setShowFuturePopup(false)}>OK</button>
            </div>
          </div>
        </div>
      )}

      {/* Actions */}
      <div style={{ display: 'flex', gap: 8, marginTop: 16, alignItems: 'center' }}>
        {(() => {
          const blks = config.blocks || [];
          const blkSum = blks.reduce((s, b) => s + (parseFloat(b.capacity_mw) || 0), 0);
          const nomCap = parseFloat(config.capacity) || 0;
          const blocksMismatch = isSolar && blks.length > 0 && Math.abs(blkSum - nomCap) >= 0.01;
          const saveDisabled = !dirty || blocksMismatch;
          return (
            <button className="btn-accent" onClick={save} disabled={saveDisabled}>
              {blocksMismatch ? 'Block capacity mismatch' : dirty ? 'Save changes' : 'Saved'}
            </button>
          );
        })()}
        <div style={{ flex: 1 }} />
        {isConfirming ? (
          <>
            <button className="btn-card danger" onClick={onConfirmDelete}>Confirm delete</button>
            <button className="btn-card" onClick={onCancelDelete}>Cancel</button>
          </>
        ) : (
          <button className="btn-card danger" onClick={onRequestDelete}>Delete asset</button>
        )}
      </div>
    </div>
  );
};

// ===========================================================================
// SolarDegradationSection — summary line + button to open modal
// ===========================================================================
const computeCumulative = (deg) => {
  const cum = [];
  let factor = 1;
  for (let i = 0; i < deg.length; i++) {
    factor *= (1 - (parseFloat(deg[i]) || 0) / 100);
    cum.push(Math.round(factor * 10000) / 100); // % with 2 decimals
  }
  return cum;
};

const SolarDegradationSection = ({ degradation, manufacturer, panelModel, onChange }) => {
  const [showModal, setShowModal] = useState(false);

  const cum = computeCumulative(degradation);
  const y25 = cum.length >= 25 ? cum[24] : null;
  const y1 = degradation.length > 0 ? parseFloat(degradation[0]) : null;
  const yRest = degradation.length > 1 ? parseFloat(degradation[1]) : null;

  const hasDeg = degradation.length > 0;
  const summary = hasDeg
    ? `${y1 != null ? y1 : '?'}% Y1, ${yRest != null ? yRest : '?'}%/yr → ${y25 != null ? y25 : '?'}% at Y25`
    : 'Not configured';

  const borderColor = hasDeg ? '#4ecca3' : '#334155';

  return (
    <div style={{ marginTop: 16 }}>
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: 8,
      }}>
        <div style={{
          fontSize: 11,
          textTransform: 'uppercase',
          letterSpacing: '0.5px',
          color: '#94a3b8',
        }}>
          Degradation
        </div>
      </div>

      <div style={{
        background: '#0f172a',
        border: `1px solid ${borderColor}`,
        borderRadius: 6,
        padding: '10px 14px',
        display: 'flex',
        alignItems: 'center',
        gap: 10,
      }}>
        {hasDeg && <div style={{ color: '#4ecca3', fontSize: 12 }}>✓</div>}
        <div style={{
          flex: 1,
          fontSize: 12,
          color: hasDeg ? '#e2e8f0' : '#94a3b8',
          fontFamily: "'JetBrains Mono', monospace",
          fontStyle: hasDeg ? 'normal' : 'italic',
        }}>
          {summary}
        </div>
        <button
          onClick={() => setShowModal(true)}
          style={{
            background: 'transparent',
            border: `1px solid ${hasDeg ? '#4ecca3' : '#334155'}`,
            color: hasDeg ? '#4ecca3' : '#94a3b8',
            borderRadius: 4,
            padding: '4px 10px',
            fontSize: 11,
            cursor: 'pointer',
            fontFamily: 'inherit',
          }}
        >
          {hasDeg ? 'Edit' : 'Configure'}
        </button>
      </div>

      {showModal && (
        <DegradationModal
          degradation={degradation}
          manufacturer={manufacturer}
          panelModel={panelModel}
          onSave={(deg) => { onChange(deg); setShowModal(false); }}
          onClose={() => setShowModal(false)}
        />
      )}
    </div>
  );
};

// ===========================================================================
// DegradationModal — year 1-25 table + quick-fill + chart
// ===========================================================================
const DegradationModal = ({ degradation, manufacturer, panelModel, onSave, onClose }) => {
  // Initialize 25 years from existing data or empty
  const initDeg = () => {
    const d = [];
    for (let i = 0; i < 25; i++) {
      d.push(degradation[i] != null ? String(degradation[i]) : '');
    }
    return d;
  };
  const [deg, setDeg] = useState(initDeg);
  const [quickY1, setQuickY1] = useState(degradation[0] != null ? String(degradation[0]) : '2.5');
  const [quickRest, setQuickRest] = useState(degradation[1] != null ? String(degradation[1]) : '0.55');

  const updateYear = (idx, val) => {
    const next = [...deg];
    next[idx] = val;
    setDeg(next);
  };

  const applyQuickFill = () => {
    const next = [];
    next[0] = quickY1;
    for (let i = 1; i < 25; i++) {
      next[i] = quickRest;
    }
    setDeg(next);
  };

  const cum = computeCumulative(deg);

  // SVG chart dimensions
  const chartW = 560;
  const chartH = 160;
  const padL = 40;
  const padR = 10;
  const padT = 10;
  const padB = 30;
  const plotW = chartW - padL - padR;
  const plotH = chartH - padT - padB;

  // Y axis: 50% to 100%
  const yMin = 50;
  const yMax = 100;
  const points = cum.map((v, i) => {
    const x = padL + (i / 24) * plotW;
    const y = padT + plotH - ((Math.max(v, yMin) - yMin) / (yMax - yMin)) * plotH;
    return `${x},${y}`;
  }).join(' ');

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" style={{ maxWidth: 900 }} onClick={(e) => e.stopPropagation()}>
        <h3>Panel Degradation — 25 Year Profile</h3>

        {/* Manufacturer + panel info */}
        <div style={{ display: 'flex', gap: 12, marginBottom: 16 }}>
          <div style={{ flex: 1, fontSize: 12, color: '#94a3b8' }}>
            {manufacturer && <span>Manufacturer: <strong style={{ color: '#e2e8f0' }}>{manufacturer}</strong> · </span>}
            {panelModel && <span>Panel: <strong style={{ color: '#e2e8f0' }}>{panelModel}</strong></span>}
            {!manufacturer && !panelModel && <span style={{ fontStyle: 'italic' }}>Set manufacturer and panel model in the fields above for reference.</span>}
          </div>
        </div>

        {/* Quick fill */}
        <div style={{
          background: '#1e293b',
          border: '1px solid #334155',
          borderRadius: 4,
          padding: 10,
          marginBottom: 14,
          display: 'flex',
          gap: 12,
          alignItems: 'flex-end',
          flexWrap: 'wrap',
        }}>
          <div style={{ fontSize: 10, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.5px', alignSelf: 'center' }}>
            Quick fill
          </div>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label style={{ fontSize: 10 }}>Year 1 (%)</label>
            <input type="number" step="0.01" value={quickY1} onChange={(e) => setQuickY1(e.target.value)} style={{ width: 80 }} />
          </div>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label style={{ fontSize: 10 }}>Years 2-25 (%)</label>
            <input type="number" step="0.01" value={quickRest} onChange={(e) => setQuickRest(e.target.value)} style={{ width: 80 }} />
          </div>
          <button className="btn-accent" onClick={applyQuickFill} style={{ fontSize: 11, padding: '6px 12px' }}>
            Apply
          </button>
        </div>

        {/* Chart */}
        <div style={{
          background: '#0f172a',
          border: '1px solid #334155',
          borderRadius: 4,
          padding: 8,
          marginBottom: 14,
        }}>
          <svg width={chartW} height={chartH} style={{ display: 'block' }}>
            {/* Grid lines */}
            {[50, 60, 70, 80, 90, 100].map((v) => {
              const y = padT + plotH - ((v - yMin) / (yMax - yMin)) * plotH;
              return (
                <g key={v}>
                  <line x1={padL} y1={y} x2={padL + plotW} y2={y} stroke="#334155" strokeWidth="0.5" />
                  <text x={padL - 4} y={y + 3} textAnchor="end" fill="#94a3b8" fontSize="9" fontFamily="JetBrains Mono, monospace">{v}%</text>
                </g>
              );
            })}
            {/* X axis labels */}
            {[1, 5, 10, 15, 20, 25].map((yr) => {
              const x = padL + ((yr - 1) / 24) * plotW;
              return (
                <text key={yr} x={x} y={chartH - 5} textAnchor="middle" fill="#94a3b8" fontSize="9" fontFamily="JetBrains Mono, monospace">
                  Y{yr}
                </text>
              );
            })}
            {/* Line */}
            {cum.length > 0 && (
              <polyline points={points} fill="none" stroke="#4ecca3" strokeWidth="2" />
            )}
            {/* Dots */}
            {cum.map((v, i) => {
              const x = padL + (i / 24) * plotW;
              const y = padT + plotH - ((Math.max(v, yMin) - yMin) / (yMax - yMin)) * plotH;
              return <circle key={i} cx={x} cy={y} r="3" fill="#4ecca3" />;
            })}
          </svg>
        </div>

        {/* Table — 5 columns × 5 rows */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(5, 1fr)',
          gap: 4,
          marginBottom: 14,
          fontFamily: "'JetBrains Mono', monospace",
          fontSize: 11,
        }}>
          {deg.map((val, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <span style={{ color: '#94a3b8', width: 28, textAlign: 'right' }}>Y{i + 1}</span>
              <input
                type="number"
                step="0.01"
                value={val}
                onChange={(e) => updateYear(i, e.target.value)}
                style={{ width: 56, fontSize: 11, textAlign: 'right', fontFamily: 'inherit' }}
              />
              <span style={{ color: '#94a3b8' }}>%</span>
              <span style={{ color: '#4ecca3', fontSize: 10, width: 38, textAlign: 'right' }}>
                {cum[i] != null ? `${cum[i]}%` : ''}
              </span>
            </div>
          ))}
        </div>

        <div className="form-actions">
          <button type="button" className="btn-secondary" onClick={onClose}>Cancel</button>
          <button
            type="button"
            className="btn-primary"
            onClick={() => onSave(deg.map((v) => v !== '' ? parseFloat(v) : null).filter((v) => v != null))}
          >
            Save degradation
          </button>
        </div>
      </div>
    </div>
  );
};

// ===========================================================================
// SolarBlocksSection — up to 2 plant description blocks for reporting
// ===========================================================================
const ARRANGEMENT_OPTIONS = [
  'Fixed tilt',
  'East-West',
  '1-Axis East-West',
  '2-Axis',
];

const emptyBlock = () => ({
  capacity_mw: '',
  arrangement: 'Fixed tilt',
  panels: '',
  inverters: '',
});

const SolarBlocksSection = ({ blocks, nominalCapacity, onChange }) => {
  const [expanded, setExpanded] = useState(null); // 0 or 1

  // Plausibility: sum of block capacities vs nominal
  const blockSum = blocks.reduce((s, b) => s + (parseFloat(b.capacity_mw) || 0), 0);
  const hasBlocks = blocks.length > 0;
  const sumMatches = hasBlocks && Math.abs(blockSum - nominalCapacity) < 0.01;
  const sumColor = !hasBlocks ? '#94a3b8' : sumMatches ? '#4ecca3' : '#fbbf24';

  const updateBlock = (idx, patch) => {
    const next = [...blocks];
    next[idx] = { ...next[idx], ...patch };
    onChange(next);
  };

  const addBlock = () => {
    if (blocks.length >= 2) return;
    const next = [...blocks, emptyBlock()];
    onChange(next);
    setExpanded(next.length - 1);
  };

  const removeBlock = (idx) => {
    const next = blocks.filter((_, i) => i !== idx);
    onChange(next);
    setExpanded(null);
  };

  return (
    <div style={{ marginTop: 16 }}>
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: 8,
      }}>
        <div style={{
          fontSize: 11,
          textTransform: 'uppercase',
          letterSpacing: '0.5px',
          color: '#94a3b8',
        }}>
          Plant blocks (description)
        </div>
        {blocks.length < 2 && (
          <button
            onClick={addBlock}
            style={{
              background: 'transparent',
              border: '1px dashed #4ecca3',
              color: '#4ecca3',
              borderRadius: 4,
              padding: '4px 10px',
              fontSize: 11,
              cursor: 'pointer',
              fontFamily: 'inherit',
            }}
          >
            + Add Block {blocks.length + 1}
          </button>
        )}
      </div>

      {hasBlocks && (
        <div style={{
          fontSize: 11,
          fontFamily: "'JetBrains Mono', monospace",
          color: sumColor,
          marginBottom: 8,
        }}>
          <span style={{ marginRight: 6 }}>{sumMatches ? '✓' : '⚠'}</span>
          Block total: {blockSum} MWac {nominalCapacity > 0 ? `/ ${nominalCapacity} MWac nominal` : ''}
          {!sumMatches && hasBlocks && <span style={{ color: '#fbbf24', marginLeft: 8 }}>(must match nominal to save)</span>}
        </div>
      )}

      {blocks.length === 0 && (
        <div style={{
          fontSize: 12,
          color: '#94a3b8',
          fontStyle: 'italic',
          padding: '8px 0',
        }}>
          No plant blocks defined. Add blocks to describe array sections (fixed tilt, tracking, etc.) for reporting.
        </div>
      )}

      {blocks.map((block, idx) => {
        const isExpanded = expanded === idx;
        return (
          <div
            key={idx}
            style={{
              background: '#0f172a',
              border: `1px solid ${isExpanded ? '#4ecca3' : '#334155'}`,
              borderRadius: 6,
              marginBottom: 8,
              overflow: 'hidden',
            }}
          >
            {/* Block header */}
            <div
              style={{
                padding: '10px 14px',
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                cursor: 'pointer',
              }}
              onClick={() => setExpanded(isExpanded ? null : idx)}
            >
              <div style={{
                background: '#D4A843',
                color: '#0f172a',
                padding: '2px 8px',
                borderRadius: 3,
                fontSize: 10,
                fontWeight: 700,
                fontFamily: "'JetBrains Mono', monospace",
              }}>
                Block {idx + 1}
              </div>
              <div style={{ flex: 1, fontSize: 12, color: '#e2e8f0' }}>
                {block.capacity_mw ? `${block.capacity_mw} MWac` : '(not configured)'}
                {block.arrangement && block.arrangement !== 'Fixed tilt' ? ` · ${block.arrangement}` : block.arrangement ? ` · ${block.arrangement}` : ''}
              </div>
              <div style={{
                color: '#94a3b8',
                fontSize: 12,
                transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
                transition: 'transform 0.15s',
              }}>▶</div>
            </div>

            {/* Block detail */}
            {isExpanded && (
              <div style={{
                padding: '12px 14px',
                borderTop: '1px solid #334155',
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                gap: 12,
              }}>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label>Capacity</label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <input
                      type="number"
                      step="0.1"
                      value={block.capacity_mw}
                      onChange={(e) => updateBlock(idx, { capacity_mw: e.target.value })}
                      style={{ flex: 1 }}
                    />
                    <span style={{ fontSize: 12, color: '#94a3b8' }}>MWac</span>
                  </div>
                </div>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label>Panel arrangement</label>
                  <select
                    value={block.arrangement}
                    onChange={(e) => updateBlock(idx, { arrangement: e.target.value })}
                    style={{ width: '100%' }}
                  >
                    {ARRANGEMENT_OPTIONS.map((opt) => (
                      <option key={opt} value={opt}>{opt}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label>Panels</label>
                  <input
                    type="text"
                    value={block.panels}
                    onChange={(e) => updateBlock(idx, { panels: e.target.value })}
                    placeholder="e.g. JA Solar 580W Mono, 51 724 panels"
                  />
                </div>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label>Inverters</label>
                  <input
                    type="text"
                    value={block.inverters}
                    onChange={(e) => updateBlock(idx, { inverters: e.target.value })}
                    placeholder="e.g. Sungrow SG250HX, 120 units"
                  />
                </div>
                <div style={{ gridColumn: '1 / -1', display: 'flex', justifyContent: 'flex-end' }}>
                  <button
                    className="btn-card danger"
                    onClick={() => removeBlock(idx)}
                    style={{ fontSize: 11 }}
                  >
                    Remove Block {idx + 1}
                  </button>
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

// ===========================================================================
// TimeseriesLinkedCard — summary with read-only capacity + two-step delete
// ===========================================================================
const TimeseriesLinkedCard = ({ asset, onRemove, onCapacityChange }) => {
  const zone = asset.timeseries_timezone || 'UTC';
  const fmtTs = (iso) => {
    if (!iso) return '—';
    try {
      return DateTime.fromISO(iso, { setZone: true }).setZone(zone).toFormat('yyyy-MM-dd HH:mm');
    } catch { return iso; }
  };

  const [displayUnit, setDisplayUnit] = useState('MWac');
  const [displayCapacity, setDisplayCapacity] = useState(asset.config?.capacity || '');
  const [stats, setStats] = useState(null);
  const [confirmRemove, setConfirmRemove] = useState(false);

  useEffect(() => {
    if (!asset.timeseries_id) return;
    fetch(`${API}/api/timeseries/${asset.timeseries_id}/stats`)
      .then((r) => r.ok ? r.json() : null)
      .then((d) => setStats(d))
      .catch(() => {});
  }, [asset.timeseries_id]);

  // Update local + bubble to parent config
  const handleCapacityChange = (val) => {
    setDisplayCapacity(val);
    if (onCapacityChange) onCapacityChange(val);
  };

  const capMw = parseFloat(displayCapacity) || 0;
  const unitMultiplier = displayUnit === 'kWac' ? 1000 : 1;
  const scale = capMw * unitMultiplier;

  const fmtStat = (v) => {
    if (v == null || !scale) return '—';
    const scaled = v * scale;
    return scaled >= 1000 ? `${(scaled / 1000).toFixed(1)}k` : scaled.toFixed(2);
  };

  const srcUnit = asset.source_unit || asset.timeseries_unit || 'MW';
  const srcInfo = asset.source_capacity_mw
    ? `Normalised from ${asset.source_capacity_mw} ${srcUnit}ac`
    : `Reference: ${asset.reference_capacity_mw} MW`;

  return (
    <div style={{ background: '#0f172a', border: '1px solid #4ecca3', borderRadius: 6, padding: 14 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
        <div style={{ color: '#4ecca3', fontSize: 14 }}>✓</div>
        <div style={{ fontSize: 13, fontWeight: 600, color: '#e2e8f0', flex: 1 }}>{asset.timeseries_name}</div>
        {confirmRemove ? (
          <div style={{ display: 'flex', gap: 4 }}>
            <button className="btn-card danger" onClick={() => { setConfirmRemove(false); if (onRemove) onRemove(); }}>Confirm delete</button>
            <button className="btn-card" onClick={() => setConfirmRemove(false)}>Cancel</button>
          </div>
        ) : (
          <button className="btn-card danger" onClick={() => setConfirmRemove(true)}>Remove</button>
        )}
      </div>

      {/* Source metadata */}
      <div style={{ fontSize: 11, color: '#94a3b8', fontFamily: "'JetBrains Mono', monospace", display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '4px 12px', marginBottom: 10 }}>
        <div>{srcInfo}</div>
        <div>source unit: {srcUnit}ac</div>
        <div>interval: {asset.interval_minutes} min</div>
        <div>rows: {asset.timeseries_rows}</div>
        <div>timezone: {zone}</div>
        {asset.time_start && <div>from: {fmtTs(asset.time_start)}</div>}
        {asset.time_end && <div>to: {fmtTs(asset.time_end)}</div>}
      </div>

      {/* Plant sizing + display */}
      <div style={{ background: '#1e293b', border: '1px solid #334155', borderRadius: 4, padding: 10, marginBottom: 10 }}>
        <div style={{ fontSize: 10, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 6 }}>Plant sizing &amp; display</div>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label style={{ fontSize: 10 }}>Installed capacity</label>
            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <input
                type="number"
                step="0.1"
                min="0"
                value={displayCapacity}
                onChange={(e) => handleCapacityChange(e.target.value)}
                style={{ width: 100, height: 34, fontFamily: "'JetBrains Mono', monospace", fontWeight: 600 }}
              />
              <span style={{ fontSize: 11, color: '#94a3b8' }}>MWac</span>
            </div>
          </div>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label style={{ fontSize: 10 }}>Display unit</label>
            <select value={displayUnit} onChange={(e) => setDisplayUnit(e.target.value)} style={{ height: 34 }}>
              <option value="kWac">kWac</option>
              <option value="MWac">MWac</option>
            </select>
          </div>
          {stats && scale > 0 && (
            <div style={{ fontSize: 11, color: '#e2e8f0', fontFamily: "'JetBrains Mono', monospace", display: 'flex', gap: 16, flexWrap: 'wrap', alignSelf: 'flex-end', paddingBottom: 6 }}>
              <span>peak: {fmtStat(stats.max)} {displayUnit}</span>
              <span>mean: {fmtStat(stats.mean)} {displayUnit}</span>
              <span>CF: {stats.max > 0 ? ((stats.mean / stats.max) * 100).toFixed(1) : '—'}%</span>
            </div>
          )}
        </div>
      </div>

      <div style={{ fontSize: 11, color: '#94a3b8', fontStyle: 'italic' }}>Chart + heatmap preview — coming in Step 4.</div>
    </div>
  );
};

// ===========================================================================
// ConnectorEditorModal — per-asset connector configuration with voltage + direction
// ===========================================================================
const DIRECTION_OPTIONS = ['out', 'in', 'bidirectional'];
const DIRECTION_LABELS = { out: '→ Out', in: '← In', bidirectional: '↔ Bi' };
const DIRECTION_COLORS = { out: '#4ecca3', in: '#60a5fa', bidirectional: '#d97706' };

const ConnectorEditorModal = ({ asset, connectors, savedConnectors, frameColor, icon, voltage, category, onSave, onClose }) => {
  // Merge saved config onto definition connectors — default all to disabled unless previously saved
  const [localConnectors, setLocalConnectors] = useState(() => {
    const hasSaved = savedConnectors && Array.isArray(savedConnectors) && savedConnectors.length > 0;
    const savedMap = {};
    if (hasSaved) {
      savedConnectors.forEach((c) => { savedMap[c.id] = c; });
    }
    return connectors.map((c) => {
      if (savedMap[c.id]) {
        // Restore saved state
        return { ...c, ...savedMap[c.id] };
      }
      // No saved state — start disabled
      return {
        ...c,
        enabled: false,
        favorite: false,
        direction: category === 'generation' ? 'out' : category === 'consumption' ? 'in' : 'out',
      };
    });
  });

  const toggleEnabled = (id) => {
    setLocalConnectors((prev) =>
      prev.map((c) => c.id === id ? { ...c, enabled: !c.enabled } : c)
    );
  };

  const toggleFavorite = (id) => {
    setLocalConnectors((prev) =>
      prev.map((c) => c.id === id ? { ...c, favorite: !c.favorite } : c)
    );
  };

  const setDirection = (id, dir) => {
    setLocalConnectors((prev) =>
      prev.map((c) => c.id === id ? { ...c, direction: dir } : c)
    );
  };

  const enabledCount = localConnectors.filter((c) => c.enabled).length;
  const favCount = localConnectors.filter((c) => c.favorite).length;

  // Plausibility: generation needs at least 1 power out
  const isGeneration = category === 'generation';
  const powerOutCount = localConnectors.filter((c) => c.enabled && (c.direction === 'out' || c.direction === 'bidirectional')).length;
  const plausible = isGeneration ? powerOutCount >= 1 : enabledCount >= 1;
  const plausibilityMsg = isGeneration && powerOutCount < 1
    ? 'At least 1 power output connector required for generation assets'
    : !plausible
      ? 'At least 1 connector must be enabled'
      : null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" style={{ maxWidth: 780 }} onClick={(e) => e.stopPropagation()}>
        <h3>Connector Configuration — {asset.name}</h3>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginTop: -8, marginBottom: 16 }}>
          <span style={{
            background: frameColor,
            color: '#0f172a',
            padding: '2px 10px',
            borderRadius: 3,
            fontSize: 11,
            fontWeight: 700,
            fontFamily: "'JetBrains Mono', monospace",
          }}>
            {voltage}
          </span>
          <span style={{ fontSize: 12, color: '#94a3b8' }}>
            {isGeneration ? 'Power generation — min 1 output required' : 'Configure connection points'}
          </span>
        </div>

        <div style={{ display: 'flex', gap: 20, alignItems: 'flex-start' }}>
          {/* Interactive shape preview */}
          <div style={{
            background: '#0f172a',
            border: '1px solid #334155',
            borderRadius: 6,
            padding: 12,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}>
            <ShapePreview
              width={100}
              height={100}
              frameColor={frameColor}
              connectorColor={frameColor}
              connectors={localConnectors}
              icon={icon}
              name={asset.name}
              interactive={true}
              onConnectorClick={toggleFavorite}
              scale={1.1}
            />
          </div>

          {/* Connector list */}
          <div style={{ flex: 1 }}>
            <div style={{
              fontSize: 10,
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              color: '#94a3b8',
              marginBottom: 8,
            }}>
              {enabledCount} enabled · {favCount} snap points
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              {['top', 'right', 'bottom', 'left'].map((side) =>
                localConnectors
                  .filter((c) => c.side === side)
                  .sort((a, b) => a.position - b.position)
                  .map((c) => (
                    <div
                      key={c.id}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 6,
                        padding: '4px 8px',
                        borderRadius: 3,
                        background: c.enabled ? '#1e2a4a' : 'transparent',
                        border: `1px solid ${c.enabled ? frameColor + '33' : '#253348'}`,
                        transition: 'all 0.12s',
                        fontSize: 11,
                      }}
                    >
                      {/* Enable toggle */}
                      <span
                        onClick={() => toggleEnabled(c.id)}
                        style={{
                          width: 10,
                          height: 10,
                          borderRadius: '50%',
                          background: c.enabled ? frameColor : '#334155',
                          cursor: 'pointer',
                          flexShrink: 0,
                          border: c.enabled ? 'none' : '1px solid #475569',
                        }}
                      />
                      {/* ID */}
                      <span style={{
                        width: 60,
                        color: c.enabled ? '#e2e8f0' : '#475569',
                        fontFamily: "'JetBrains Mono', monospace",
                        fontSize: 10,
                      }}>
                        {c.side}-{c.position}
                      </span>
                      {/* Favorite toggle */}
                      <span
                        onClick={() => { if (c.enabled) toggleFavorite(c.id); }}
                        style={{
                          cursor: c.enabled ? 'pointer' : 'default',
                          color: c.enabled && c.favorite ? frameColor : '#475569',
                          fontSize: 12,
                          width: 16,
                          textAlign: 'center',
                        }}
                      >
                        {c.favorite ? '★' : '☆'}
                      </span>
                      {/* Direction selector */}
                      {c.enabled && (
                        <div style={{ display: 'flex', gap: 2, marginLeft: 4 }}>
                          {DIRECTION_OPTIONS.map((dir) => (
                            <button
                              key={dir}
                              onClick={() => setDirection(c.id, dir)}
                              style={{
                                background: c.direction === dir ? DIRECTION_COLORS[dir] + '33' : 'transparent',
                                border: `1px solid ${c.direction === dir ? DIRECTION_COLORS[dir] : '#334155'}`,
                                color: c.direction === dir ? DIRECTION_COLORS[dir] : '#475569',
                                borderRadius: 3,
                                padding: '1px 6px',
                                fontSize: 9,
                                cursor: 'pointer',
                                fontFamily: 'inherit',
                              }}
                            >
                              {DIRECTION_LABELS[dir]}
                            </button>
                          ))}
                        </div>
                      )}
                      {/* Voltage badge for enabled */}
                      {c.enabled && voltage && (
                        <span style={{
                          marginLeft: 'auto',
                          fontSize: 9,
                          color: '#94a3b8',
                          fontFamily: "'JetBrains Mono', monospace",
                        }}>
                          {voltage}
                        </span>
                      )}
                    </div>
                  ))
              )}
            </div>
          </div>
        </div>

        {/* Plausibility message */}
        {plausibilityMsg && (
          <div style={{
            marginTop: 12,
            padding: '8px 12px',
            background: '#7f1d1d',
            color: '#fecaca',
            borderRadius: 4,
            fontSize: 12,
          }}>
            {plausibilityMsg}
          </div>
        )}

        <div className="form-actions" style={{ marginTop: 16 }}>
          <button type="button" className="btn-secondary" onClick={onClose}>Cancel</button>
          <button
            type="button"
            className="btn-primary"
            disabled={!plausible}
            onClick={() => onSave(localConnectors.map((c) => ({
              id: c.id, side: c.side, position: c.position,
              enabled: c.enabled, favorite: c.favorite,
              direction: c.direction, voltage: voltage,
            })))}
          >
            Save Configuration
          </button>
        </div>
      </div>
    </div>
  );
};

// ===========================================================================
// AssetDefinitionPicker — modal to choose which asset definition to add
// ===========================================================================
const AssetDefinitionPicker = ({ definitions, onPick, onClose }) => {
  const grouped = {};
  definitions.forEach((d) => {
    (grouped[d.category] = grouped[d.category] || []).push(d);
  });

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" style={{ maxWidth: 720 }} onClick={(e) => e.stopPropagation()}>
        <h3>Add Asset</h3>
        <p style={{ fontSize: 12, color: '#94a3b8', marginTop: -8, marginBottom: 16 }}>
          Pick a definition from the library. You can rename and configure it after adding.
        </p>
        {CATEGORIES.map((cat) => {
          const items = grouped[cat.id] || [];
          if (items.length === 0) return null;
          return (
            <div key={cat.id} style={{ marginBottom: 16 }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                fontSize: 10,
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                color: '#94a3b8',
                marginBottom: 8,
              }}>
                <span style={{ width: 8, height: 8, borderRadius: '50%', background: cat.color }} />
                {cat.label}
              </div>
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
                gap: 8,
              }}>
                {items.map((d) => (
                  <button
                    key={d.id}
                    onClick={() => onPick(d.id)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 10,
                      padding: '10px 12px',
                      background: '#1e293b',
                      border: `1px solid #334155`,
                      borderLeft: `3px solid ${cat.color}`,
                      borderRadius: 4,
                      color: '#e2e8f0',
                      cursor: 'pointer',
                      fontFamily: 'inherit',
                      fontSize: 13,
                      textAlign: 'left',
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = '#1e2a4a'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = '#1e293b'; }}
                  >
                    <span style={{ fontSize: 20 }}>{d.icon || '▢'}</span>
                    <span style={{ flex: 1 }}>{d.name}</span>
                  </button>
                ))}
              </div>
            </div>
          );
        })}
        <div className="form-actions">
          <button type="button" className="btn-secondary" onClick={onClose}>Cancel</button>
        </div>
      </div>
    </div>
  );
};

// ===========================================================================
// VersionCard — one expandable card per version, with revision strip
// ===========================================================================
const VersionCard = ({
  version, expanded, editing, revisions, expandedRevisionId, confirmDelete,
  onToggle, onEdit, onCancelEdit, onSaveEdit,
  onRequestDelete, onConfirmDelete, onCancelDelete,
  onCreateRevision, onExpandRevision, onUpdateRevision,
  onRequestDeleteRevision, onConfirmDeleteRevision, onOpenCanvasViewer,
}) => {
  const v = version;
  const cardStatus = STATUS_COLORS[v.status] || '#4ecca3';
  const isConfirming = confirmDelete === `version:${v.id}`;
  const revs = revisions || [];
  const usedLetters = new Set(revs.map((r) => r.rev_letter));
  const canAddRevision = usedLetters.size < 4;

  return (
    <div style={{
      background: '#1e293b',
      border: `1px solid #334155`,
      borderLeft: `4px solid ${cardStatus}`,
      borderRadius: 6,
      overflow: 'hidden',
    }}>
      {/* Header row */}
      <div style={{
        padding: '12px 16px',
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        cursor: 'pointer',
      }} onClick={onToggle}>
        <div style={{
          background: cardStatus,
          color: '#0f172a',
          fontFamily: "'JetBrains Mono', monospace",
          fontWeight: 700,
          fontSize: 12,
          padding: '4px 8px',
          borderRadius: 4,
          minWidth: 32,
          textAlign: 'center',
        }}>
          v{v.version_number}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{
            fontSize: 15,
            fontWeight: 600,
            color: '#e2e8f0',
            fontFamily: "'Space Grotesk', sans-serif",
          }}>
            {v.name}
          </div>
          {v.description && (
            <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {v.description}
            </div>
          )}
        </div>
        <div style={{ display: 'flex', gap: 16, fontSize: 11, color: '#94a3b8', fontFamily: "'JetBrains Mono', monospace", alignItems: 'center' }}>
          {v.version_date && <span>{fmtDate(v.version_date)}</span>}
          <span>updated {fmtTs(v.updated_at)}</span>
          <span style={{ color: cardStatus, fontWeight: 600 }}>{v.status}</span>
          <span>{v.revision_count || 0}/4 revs</span>
        </div>
        <div style={{ display: 'flex', gap: 6 }} onClick={(e) => e.stopPropagation()}>
          <button className="btn-card" onClick={onEdit}>Edit</button>
          {isConfirming ? (
            <>
              <button className="btn-card danger" onClick={onConfirmDelete}>Confirm</button>
              <button className="btn-card" onClick={onCancelDelete}>Cancel</button>
            </>
          ) : (
            <button className="btn-card danger" onClick={onRequestDelete}>Delete</button>
          )}
        </div>
        <div style={{ color: '#94a3b8', fontSize: 12, transform: expanded ? 'rotate(90deg)' : 'rotate(0deg)', transition: 'transform 0.15s' }}>▶</div>
      </div>

      {/* Expanded body */}
      {expanded && (
        <div style={{ padding: '12px 16px 16px 16px', borderTop: `1px solid #334155`, background: '#0f172a' }}>
          {/* Inline version edit panel (when editing) */}
          {editing && (
            <VersionEditPanel
              version={v}
              onSave={onSaveEdit}
              onCancel={onCancelEdit}
            />
          )}
          {/* Revision chip strip */}
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'stretch' }}>
            {['A', 'B', 'C', 'D'].map((letter) => {
              const rev = revs.find((r) => r.rev_letter === letter);
              if (rev) {
                const isExpanded = expandedRevisionId === rev.id;
                const revStatus = STATUS_COLORS[rev.status] || '#4ecca3';
                return (
                  <button
                    key={letter}
                    onClick={() => onExpandRevision(rev.id)}
                    style={{
                      background: isExpanded ? '#1e2a4a' : '#1e293b',
                      border: `1px solid ${isExpanded ? '#4ecca3' : '#334155'}`,
                      borderRadius: 4,
                      padding: '8px 12px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 10,
                      cursor: 'pointer',
                      minWidth: 180,
                      textAlign: 'left',
                      fontFamily: 'inherit',
                      color: '#e2e8f0',
                    }}
                  >
                    <div style={{
                      background: revStatus,
                      color: '#0f172a',
                      fontFamily: "'JetBrains Mono', monospace",
                      fontWeight: 700,
                      fontSize: 11,
                      padding: '2px 6px',
                      borderRadius: 3,
                    }}>
                      Rev {letter}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 12, color: '#e2e8f0', fontWeight: 500 }}>{rev.name}</div>
                      <div style={{ fontSize: 10, color: '#94a3b8', fontFamily: "'JetBrains Mono', monospace" }}>
                        {rev.status} · {fmtTs(rev.updated_at)}
                      </div>
                    </div>
                  </button>
                );
              }
              // Empty slot
              const isNext = canAddRevision && !usedLetters.has(letter) && ['A','B','C','D'].slice(0, usedLetters.size).every((l) => usedLetters.has(l)) && letter === ['A','B','C','D'][usedLetters.size];
              return (
                <button
                  key={letter}
                  onClick={isNext ? onCreateRevision : undefined}
                  disabled={!isNext}
                  style={{
                    background: 'transparent',
                    border: `1px dashed ${isNext ? '#4ecca3' : '#334155'}`,
                    borderRadius: 4,
                    padding: '8px 12px',
                    minWidth: 180,
                    color: isNext ? '#4ecca3' : '#475569',
                    fontSize: 12,
                    cursor: isNext ? 'pointer' : 'not-allowed',
                    fontFamily: 'inherit',
                  }}
                >
                  {isNext ? `+ Rev ${letter}` : `Rev ${letter}`}
                </button>
              );
            })}
          </div>

          {/* Expanded revision sub-panel */}
          {expandedRevisionId && revs.find((r) => r.id === expandedRevisionId) && (
            <RevisionPanel
              revision={revs.find((r) => r.id === expandedRevisionId)}
              confirmDelete={confirmDelete}
              onUpdate={(patch) => onUpdateRevision(expandedRevisionId, patch)}
              onRequestDelete={() => onRequestDeleteRevision(expandedRevisionId)}
              onConfirmDelete={() => onConfirmDeleteRevision(expandedRevisionId)}
              onCancelDelete={() => onCancelDelete()}
              onOpenCanvasViewer={() => onOpenCanvasViewer(revs.find((r) => r.id === expandedRevisionId))}
            />
          )}
        </div>
      )}
    </div>
  );
};

// ===========================================================================
// VersionEditPanel — inline editor for a single version
// ===========================================================================
const VersionEditPanel = ({ version, onSave, onCancel }) => {
  const [name, setName] = useState(version.name || '');
  const [description, setDescription] = useState(version.description || '');
  const [versionDate, setVersionDate] = useState(fmtDate(version.version_date));
  const [status, setStatus] = useState(version.status || 'draft');

  useEffect(() => {
    setName(version.name || '');
    setDescription(version.description || '');
    setVersionDate(fmtDate(version.version_date));
    setStatus(version.status || 'draft');
  }, [version.id, version.updated_at, version.name, version.description, version.version_date, version.status]);

  const submit = () => {
    onSave({
      name: name.trim(),
      description: description.trim() || null,
      version_date: versionDate || null,
      status,
    });
  };

  return (
    <div style={{
      marginBottom: 12,
      background: '#1e293b',
      border: '1px solid #4ecca3',
      borderRadius: 6,
      padding: 16,
    }}>
      <div style={{ fontSize: 12, color: '#4ecca3', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 10, fontFamily: "'JetBrains Mono', monospace" }}>
        Editing v{version.version_number}
      </div>
      <div className="form-group">
        <label><span className={name.trim() ? 'valid-star' : 'required-star'}>*</span> Name</label>
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g. Baseline 2026, Net-Zero scenario"
          style={{ width: '100%', fontFamily: 'inherit' }}
        />
      </div>
      <div className="form-group">
        <label>Description</label>
        <textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="What's in this version? Key assumptions, scope…"
          rows={3}
          style={{ width: '100%', fontFamily: 'inherit' }}
        />
      </div>
      <div className="form-row">
        <div className="form-group">
          <label>Version Date</label>
          <input
            type="date"
            value={versionDate}
            onChange={(e) => setVersionDate(e.target.value)}
          />
        </div>
        <div className="form-group">
          <label>Status</label>
          <select value={status} onChange={(e) => setStatus(e.target.value)}>
            {VERSION_STATUS_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
      </div>
      <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
        <button className="btn-accent" onClick={submit} disabled={!name.trim()}>Save changes</button>
        <button className="btn-card" onClick={onCancel}>Cancel</button>
      </div>
    </div>
  );
};

// ===========================================================================
// RevisionPanel — inline editor for a single revision
// ===========================================================================
const RevisionPanel = ({ revision, confirmDelete, onUpdate, onRequestDelete, onConfirmDelete, onCancelDelete, onOpenCanvasViewer }) => {
  const [description, setDescription] = useState(revision.description || '');
  const [status, setStatus] = useState(revision.status || 'draft');
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    setDescription(revision.description || '');
    setStatus(revision.status || 'draft');
    setDirty(false);
  }, [revision.id, revision.updated_at, revision.description, revision.status]);

  const isConfirming = confirmDelete === `revision:${revision.id}`;

  const save = () => {
    onUpdate({ description, status });
    setDirty(false);
  };

  return (
    <div style={{
      marginTop: 12,
      background: '#1e293b',
      border: '1px solid #334155',
      borderRadius: 6,
      padding: 16,
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
        <div>
          <div style={{ fontSize: 14, fontWeight: 600, color: '#e2e8f0', fontFamily: "'Space Grotesk', sans-serif" }}>
            {revision.name}
          </div>
          <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 2, fontFamily: "'JetBrains Mono', monospace" }}>
            created {fmtTs(revision.created_at)} · updated {fmtTs(revision.updated_at)}
          </div>
        </div>
        <button
          onClick={onOpenCanvasViewer}
          style={{
            background: '#4ecca3',
            color: '#0f172a',
            border: 'none',
            borderRadius: 4,
            padding: '8px 16px',
            fontSize: 12,
            fontWeight: 600,
            cursor: 'pointer',
            fontFamily: 'inherit',
          }}
        >
          Canvas Editor →
        </button>
      </div>

      <div className="form-group">
        <label>Description</label>
        <textarea
          value={description}
          onChange={(e) => { setDescription(e.target.value); setDirty(true); }}
          placeholder="How does this revision differ from the others?"
          rows={3}
          style={{ width: '100%', fontFamily: 'inherit' }}
        />
      </div>

      <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
        <div className="form-group" style={{ marginBottom: 0 }}>
          <label>Status</label>
          <select value={status} onChange={(e) => { setStatus(e.target.value); setDirty(true); }}>
            {REV_STATUS_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        {dirty && (
          <button className="btn-accent" onClick={save}>Save changes</button>
        )}
        <div style={{ flex: 1 }} />
        {isConfirming ? (
          <>
            <button className="btn-card danger" onClick={onConfirmDelete}>Confirm delete</button>
            <button className="btn-card" onClick={onCancelDelete}>Cancel</button>
          </>
        ) : (
          <button className="btn-card danger" onClick={onRequestDelete}>Delete revision</button>
        )}
      </div>
    </div>
  );
};

export default ProjectsView;
